import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { Subscription } from 'rxjs';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../modal/confirmdialog/confirmdialog.component';
import { ExporttocsvService } from '../../../services/exporttocsv/exporttocsv.service';
import { TeamsService } from '../../../services/teams/teams.service';
import {
  CompanyGroupProductOnboardingRow,
  TeamCompaniesService,
} from '../../../services/teamcompanies/teamcompanies.service';
import { UsersService } from '../../../services/users/users.service';
import { VendorProductsService } from '../../../services/vendorproducts/vendorproducts.service';
import {
  buildScheduledReportValueOptions,
  ScheduledReport,
  ScheduledReportAsOfMode,
  ScheduledReportBaseEntity,
  ScheduledReportBusinessPurpose,
  ScheduledReportDeliveryChannel,
  ScheduledReportEmailDeliveryConfig,
  ScheduledReportDefinitionAggregation,
  ScheduledReportDefinitionAggregationLevel,
  ScheduledReportDefinitionColumn,
  ScheduledReportDefinitionFilter,
  ScheduledReportDefinitionSort,
  ScheduledReportFieldArgSchema,
  ScheduledReportFieldDefinition,
  ScheduledReportOutputFormat,
  ScheduledReportOutputTransform,
  ScheduledReportPayload,
  ScheduledReportPreview,
  ScheduledReportPreviewOverrides,
  ScheduledReportRun,
  ScheduledReportScheduleType,
  ScheduledReportSensitiveAccess,
  ScheduledReportSensitiveDisplayMode,
  ScheduledReportSftpSecretCatalog,
  ScheduledReportSftpSecretReference,
  ScheduledReportSftpSecretType,
  ScheduledReportSubscriptionCoverageMode,
  ScheduledReportTemplate,
  ScheduledReportTemplateColumn,
  ScheduledReportTemplateColumnOverride,
  ScheduledReportTemplateSort,
  ScheduledReportValueOption,
  ScheduledReportWindowMode,
  ScheduledReportsService,
} from '../../../services/scheduled-reports/scheduled-reports.service';
import {
  PayrollReportRerunDialogComponent,
  PayrollReportRerunDialogResult,
} from './payroll-report-rerun-dialog.component';
import {
  PayrollDashboardReportParameterDialogResult,
  PayrollDashboardReportParametersDialogComponent,
} from '../payroll-dashboards/payroll-dashboard-report-parameters-dialog.component';
import {
  PayrollSensitiveReportPreviewDialogComponent,
  PayrollSensitiveReportPreviewDialogResult,
} from './payroll-sensitive-report-preview-dialog.component';
import {
  PayrollReportSftpSecretDialogComponent,
  PayrollReportSftpSecretDialogResult,
} from './payroll-report-sftp-secret-dialog.component';

type ReportsViewMode = 'list' | 'create' | 'edit' | 'detail';
type ReportTabType = 'template' | 'flat_file';
type ScopeType = 'partner' | 'company' | 'team' | 'global';
type TemplateEnrollmentStatusFilter = 'all' | 'enrolled' | 'not_enrolled';
type ReportColumnKind = 'field' | 'template' | 'value';
type CustomReportResultMode = 'detail' | 'aggregated';
type GroupProductOnboardingReportStatusFilter = 'all' | 'pending' | 'ready';

interface ScopeOption {
  id: string;
  label: string;
  type: ScopeType;
  secondaryLabel?: string;
}

interface ProductOption {
  id: string;
  label: string;
  vendorSlug: string;
  productSlug: string;
  vendorName?: string;
  productName?: string;
}

interface VendorOption {
  value: string;
  label: string;
}

interface DatasetOption {
  key: string;
  label: string;
  enabled: boolean;
  note: string;
}

interface KnownDatasetOption {
  key: string;
  label: string;
  description: string;
}

interface PermissionSet {
  create: boolean;
  edit: boolean;
  delete: boolean;
  pauseResume: boolean;
  runManual: boolean;
  rerunWithAsOf: boolean;
  downloadArtifacts: boolean;
}

type PreviewSortTarget = 'editor' | 'detail';
type PreviewSortDirection = 'asc' | 'desc';
type DateFormatMode = 'date' | 'datetime' | 'datetime_timezone' | 'custom';
type ReportListSortColumn =
  | 'name'
  | 'reportType'
  | 'scope'
  | 'outputFormat'
  | 'deliveryChannel'
  | 'schedule'
  | 'status'
  | 'lastRunAt'
  | 'lastResult'
  | 'nextRunAt';

interface VendorSelectionDraft {
  vendorSlugs: string[];
  productIds: string[];
}

@Component({
  selector: 'app-payroll-reports',
  templateUrl: './payroll-reports.component.html',
  styleUrls: ['./payroll-reports.component.scss']
})
export class PayrollReportsComponent implements OnInit, OnDestroy {
  private readonly allTemplateVendorsValue = '__all_vendors__';
  private readonly previewRowLimit = 500;
  private readonly sensitivePreviewRowLimit = 100;
  private readonly reportDateOutputColumnHeaders = new Set(['windowstartdate', 'windowenddate']);
  private readonly reportDateOutputColumnFields = new Set([
    'windowstart',
    'window_start',
    'runtime.windowstart',
    'runtime.window_start',
    'report.windowstart',
    'report.window_start',
    'runcontext.windowstart',
    'runcontext.window_start',
    'windowend',
    'window_end',
    'runtime.windowend',
    'runtime.window_end',
    'report.windowend',
    'report.window_end',
    'runcontext.windowend',
    'runcontext.window_end',
  ]);
  private readonly knownDatasetOptions: KnownDatasetOption[] = [
    { key: 'users', label: 'Users', description: 'One row per user.' },
    { key: 'teamusers', label: 'Team Members', description: 'One row per team membership.' },
    { key: 'removedTeamusers', label: 'Removed Team Members', description: 'One row per deleted team membership.' },
    { key: 'usersVendorProducts', label: 'Raw Enrollment Events', description: 'Raw product enrollment events. A member can appear more than once for plan changes, corrections, or repeated enrollment records.' },
    { key: 'currentUsersVendorProducts', label: 'Enrollments Active at Cutoff', description: 'A point-in-time snapshot with one row per member and product active at the report cutoff. Use this to see who was enrolled on a specific date.' },
    { key: 'usersInsuredState', label: 'Insured Members', description: 'One row per insurance state record.' },
    { key: 'vendorSubscriptionCoverage', label: 'Subscription Coverage', description: 'A period-based report with one row per coverage interval that overlaps the report window. Use this to see who had coverage at any time during the selected period.' },
  ];

  protected isCompanyStateLoading = true;
  protected isBootstrapping = false;
  protected isListLoading = false;
  protected isDetailLoading = false;
  protected isPreviewLoading = false;
  protected isSaving = false;
  protected areTeamOptionsLoading = false;
  protected isGroupProductOnboardingReportLoading = false;

  protected viewMode: ReportsViewMode = 'list';
  protected activeListTab: ReportTabType = 'template';

  protected reports: ScheduledReport[] = [];
  protected templates: ScheduledReportTemplate[] = [];
  protected fieldCatalog: ScheduledReportFieldDefinition[] = [];
  protected sensitivePreviewBusinessPurposes: ScheduledReportBusinessPurpose[] = [];
  protected baseEntities: ScheduledReportBaseEntity[] = [];
  protected preview: ScheduledReportPreview | null = null;
  protected previewError = '';
  protected previewSignature = '';
  protected editorSensitivePreviewMode: ScheduledReportSensitiveDisplayMode | null = null;
  protected detailReport: ScheduledReport | null = null;
  protected reportRuns: ScheduledReportRun[] = [];
  protected selectedRun: ScheduledReportRun | null = null;
  protected detailPreview: ScheduledReportPreview | null = null;
  protected detailPreviewError = '';
  protected isDetailPreviewLoading = false;
  protected detailReportIsSensitive = false;
  protected detailSensitivePreviewMode: ScheduledReportSensitiveDisplayMode | null = null;
  protected canViewGroupProductOnboardingReport = false;
  protected groupProductOnboardingReportRows: CompanyGroupProductOnboardingRow[] = [];
  protected selectedGroupProductOnboardingReportStatusFilter: GroupProductOnboardingReportStatusFilter = 'all';
  protected pendingGroupProductOnboardingReadyKeys = new Set<string>();
  protected pendingGroupProductOnboardingDeleteKeys = new Set<string>();

  protected searchValue = '';
  protected statusFilter = 'all';
  protected scopeFilter = 'all';
  protected deliveryFilter = 'all';

  protected companies: ScopeOption[] = [];
  protected partners: ScopeOption[] = [];
  protected teams: ScopeOption[] = [];
  protected productOptions: ProductOption[] = [];
  protected sftpPasswordSecretReferences: ScheduledReportSftpSecretReference[] = [];
  protected sftpPrivateKeySecretReferences: ScheduledReportSftpSecretReference[] = [];
  protected sftpPgpPublicKeySecretReferences: ScheduledReportSftpSecretReference[] = [];
  protected readonly sftpSecretPrefixes: Record<ScheduledReportSftpSecretType, string> = {
    password: 'scheduled-reports/sftp/password/',
    private_key: 'scheduled-reports/sftp/private-key/',
    pgp_public_key: 'scheduled-reports/sftp/pgp-public-key/',
  };
  private reportValueOptions: Record<string, ScheduledReportValueOption[]> = {};
  protected validationMessages: string[] = [];
  protected readonly selectSearchThreshold = 8;
  protected readonly scopeSearchControl = new UntypedFormControl('');
  protected readonly templateSearchControl = new UntypedFormControl('');
  protected readonly templateVendorSearchControl = new UntypedFormControl('');
  protected readonly templateProductSearchControl = new UntypedFormControl('');
  protected readonly fieldSearchControl = new UntypedFormControl('');
  protected readonly semanticVendorSearchControl = new UntypedFormControl('');
  protected readonly semanticProductSearchControl = new UntypedFormControl('');
  protected readonly sortFieldSearchControl = new UntypedFormControl('');
  protected readonly previewSortState: Record<PreviewSortTarget, { header: string; direction: PreviewSortDirection } | null> = {
    editor: null,
    detail: null,
  };
  protected readonly reportListSort: Sort = {
    active: 'name',
    direction: 'asc',
  };

  protected readonly dayOptions = [
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' },
    { value: 7, label: 'Sun' },
  ];

  protected readonly reportTypeTabs: Array<{ value: ReportTabType; label: string; description: string }> = [
    {
      value: 'template',
      label: 'Canned Reports',
      description: 'Start from a guided template and only fill in the supported controls.',
    },
    {
      value: 'flat_file',
      label: 'Custom Flat-File Reports',
      description: 'Choose approved fields, filters, and sort rules without raw SQL.',
    },
  ];

  protected readonly scopeTypes: Array<{ value: ScopeType; label: string }> = [
    { value: 'partner', label: 'Partner' },
    { value: 'company', label: 'Company' },
    { value: 'team', label: 'Team' },
    { value: 'global', label: 'Global' },
  ];

  protected readonly reportColumnKinds: Array<{ value: ReportColumnKind; label: string }> = [
    { value: 'field', label: 'Field' },
    { value: 'template', label: 'Template' },
    { value: 'value', label: 'Static Value' },
  ];

  protected readonly customReportResultModes: Array<{ value: CustomReportResultMode; label: string; description: string }> = [
    {
      value: 'detail',
      label: 'Detail rows',
      description: 'Return one output row per matching base-entity record.',
    },
    {
      value: 'aggregated',
      label: 'Grouped counts',
      description: 'Return one row per group with a final count column.',
    },
  ];

  protected readonly outputTransforms: Array<{ value: ScheduledReportOutputTransform | ''; label: string }> = [
    { value: '', label: 'None' },
    { value: 'currency', label: 'Currency' },
    { value: 'decrypt', label: 'Decrypt' },
    { value: 'decrypt_digits', label: 'Decrypt digits' },
    { value: 'yes_no', label: 'Yes/No' },
  ];

  protected readonly outputFormats: Array<{ value: ScheduledReportOutputFormat; label: string }> = [
    { value: 'csv', label: 'CSV' },
    { value: 'xlsx', label: 'XLSX' },
  ];

  protected readonly dateFormatOptions: Array<{ value: DateFormatMode; label: string }> = [
    { value: 'date', label: 'Date Only' },
    { value: 'datetime', label: 'Date and Time' },
    { value: 'datetime_timezone', label: 'Date, Time, and Time Zone' },
    { value: 'custom', label: 'Custom Moment Format' },
  ];

  protected readonly reportTotalNumericFieldTypes = new Set(['number', 'integer', 'decimal', 'float', 'currency', 'money']);

  protected readonly deliveryChannels: Array<{ value: ScheduledReportDeliveryChannel; label: string }> = [
    { value: 'ftp', label: 'SFTP' },
    { value: 'email', label: 'Email' },
  ];

  protected readonly scheduleTypes: Array<{ value: ScheduledReportScheduleType; label: string }> = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
  ];

  protected readonly windowModes: Array<{ value: ScheduledReportWindowMode; label: string }> = [
    { value: 'current_day', label: 'Today' },
    { value: 'previous_day', label: 'Previous day' },
    { value: 'current_week', label: 'Current week' },
    { value: 'previous_week', label: 'Previous week' },
    { value: 'current_month', label: 'Current month' },
    { value: 'previous_month', label: 'Previous month' },
    { value: 'rolling_days', label: 'Rolling days' },
  ];

  protected readonly asOfModes: Array<{ value: ScheduledReportAsOfMode; label: string; help: string }> = [
    {
      value: 'run_time',
      label: 'Run time',
      help: 'Use the moment the report runs as the cutoff. With active-at-cutoff coverage, this means active at run time, even when the report window is previous month.',
    },
    {
      value: 'window_end',
      label: 'Window end',
      help: 'Use the window end as the historical cutoff.',
    },
    {
      value: 'manual',
      label: 'Manual for reruns only',
      help: 'Use an explicit as-of date only when rerunning the report.',
    },
  ];

  protected readonly subscriptionCoverageModes: Array<{ value: ScheduledReportSubscriptionCoverageMode; label: string; help: string }> = [
    {
      value: 'window_overlap',
      label: 'Overlaps report window',
      help: 'Count valid coverage intervals that were active at any point in the selected report window.',
    },
    {
      value: 'active_as_of',
      label: 'Active at cutoff',
      help: 'Count only coverage intervals active at the as-of cutoff, regardless of how wide the report window is.',
    },
  ];

  protected readonly templateEnrollmentStatusFilters: Array<{ value: TemplateEnrollmentStatusFilter; label: string }> = [
    { value: 'all', label: 'All' },
    { value: 'enrolled', label: 'Enrolled only' },
    { value: 'not_enrolled', label: 'Not enrolled only' },
  ];

  protected readonly defaultPermissions: PermissionSet = {
    create: true,
    edit: true,
    delete: true,
    pauseResume: true,
    runManual: true,
    rerunWithAsOf: true,
    downloadArtifacts: true,
  };

  protected readonly reportForm = this.formBuilder.group({
    name: ['', Validators.required],
    description: [''],
    status: ['draft', Validators.required],
    reportType: ['template', Validators.required],
    resultMode: ['detail'],
    scopeType: ['global', Validators.required],
    scopeIds: [[]],
    templateKey: [''],
    baseEntity: ['users'],
    outputFormat: ['csv', Validators.required],
    fileNamePattern: [this.getDefaultFileNamePattern()],
    showReportDates: [false],
    timezone: [Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles', Validators.required],
    scheduleType: ['weekly', Validators.required],
    scheduleConfig: this.formBuilder.group({
      time: ['08:00', Validators.required],
      daysOfWeek: [[1]],
      dayOfMonth: [1],
      windowMode: ['previous_month', Validators.required],
      rollingDays: [7],
    }),
    templateArgs: this.formBuilder.group({
      vendorSlugs: [[]],
      vendorSlug: [''],
      productSlugs: [[]],
      enrollmentStatusFilter: ['all'],
      includeUsersWithoutCurrentTeamMembership: [true],
    }),
    customAudience: this.formBuilder.group({
      includeUsersWithoutCurrentTeamMembership: [true],
    }),
    temporal: this.formBuilder.group({
      asOfMode: ['run_time'],
      subscriptionCoverageMode: ['window_overlap'],
    }),
    distinct: [true],
    deliveryChannel: ['email', Validators.required],
    deliveryConfig: this.formBuilder.group({
      host: [''],
      port: [22],
      username: [''],
      passwordSecretKey: [''],
      privateKeySecretKey: [''],
      folder: [''],
      fileNamePrefix: [''],
      pgpEnabled: [false],
      publicKeySecretKey: [''],
      toText: [''],
      ccText: [''],
      bccText: [''],
      subjectTemplate: [''],
      bodyTemplate: [''],
    }),
    aggregation: this.formBuilder.group({
      countHeader: ['Count'],
      countDistinctField: [''],
      countDistinctArgs: this.formBuilder.group({}),
      reportTotal: [false],
      groupBy: this.formBuilder.array([]),
    }),
    templateColumns: this.formBuilder.array([]),
    columns: this.formBuilder.array([]),
    filters: this.formBuilder.array([]),
    sort: this.formBuilder.array([]),
  });

  protected editingReportId = '';
  protected editorIncludeDemoUsers = false;
  protected detailIncludeDemoUsers = false;
  private meSubscription?: Subscription;
  private routeQueryParamsSubscription?: Subscription;
  private hasLoadedTeamOptions = false;
  private useAllTeamsLookup = false;
  private teamOptionsLoadPromise: Promise<void> | null = null;
  private datasetOptionsCache: DatasetOption[] = [];
  private datasetOptionsCacheSource: ScheduledReportBaseEntity[] | null = null;
  private groupedFieldsCache: Array<{ group: string; fields: ScheduledReportFieldDefinition[] }> = [];
  private groupedFieldsCacheBaseEntity = '';
  private groupedFieldsCacheSource: ScheduledReportFieldDefinition[] | null = null;
  private fieldArgEntriesCache = new Map<string, Array<{ key: string; schema: ScheduledReportFieldArgSchema }>>();
  private fieldArgEntriesCacheSource: ScheduledReportFieldDefinition[] | null = null;
  private initialArgKeysByGroup = new WeakMap<UntypedFormGroup, Set<string>>();
  private lastHandledRouteQueryKey = '';
  private editorSensitiveRuntimeParameters: Record<string, any> | undefined;
  private editorSensitiveAccess: ScheduledReportSensitiveAccess | null = null;
  private editorMaskedSensitivePreview: ScheduledReportPreview | null = null;
  private detailSensitivePreviewOverrides: ScheduledReportPreviewOverrides | undefined;
  private detailSensitiveAccess: ScheduledReportSensitiveAccess | null = null;
  private detailMaskedSensitivePreview: ScheduledReportPreview | null = null;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private usersService: UsersService,
    private teamsService: TeamsService,
    private teamCompaniesService: TeamCompaniesService,
    private exportToCsvService: ExporttocsvService,
    private vendorProductsService: VendorProductsService,
    private scheduledReportsService: ScheduledReportsService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.configureFormSubscriptions();
    this.routeQueryParamsSubscription = this.route.queryParamMap.subscribe(() => {
      if (!this.isCompanyStateLoading && !this.isBootstrapping) {
        void this.applyRouteContext();
      }
    });
    this.meSubscription = this.usersService.meSubscribe(async (me: any) => {
      if (!me || !this.isCompanyStateLoading) {
        return;
      }
      this.canViewGroupProductOnboardingReport = this.usersService.isAdmin();

      const cachedCompanies = this.usersService.getCachedTeamCompanies();
      if (cachedCompanies) {
        this.companies = this.normalizeCompanies(cachedCompanies);
      }

      await this.loadInitialState();
    });
  }

  ngOnDestroy(): void {
    this.clearEditorSensitivePreviewState();
    this.clearDetailSensitivePreviewState();
    this.meSubscription?.unsubscribe();
    this.routeQueryParamsSubscription?.unsubscribe();
  }

  protected get filteredReports(): ScheduledReport[] {
    const search = this.searchValue.trim().toLowerCase();
    const filtered = this.reports.filter((report) => {
      if (`${report.reportType}` !== this.activeListTab) {
        return false;
      }
      if (this.statusFilter !== 'all' && `${report.status}` !== this.statusFilter) {
        return false;
      }
      if (this.scopeFilter !== 'all' && `${report.scopeType}` !== this.scopeFilter) {
        return false;
      }
      if (this.deliveryFilter !== 'all' && `${report.deliveryChannel}` !== this.deliveryFilter) {
        return false;
      }
      if (!search) {
        return true;
      }

      return [
        report.name,
        report.description,
        this.getScopeLabel(report),
        this.getDeliveryLabel(report.deliveryChannel),
        this.getFormatLabel(report.outputFormat),
      ].some((value) => `${value || ''}`.toLowerCase().includes(search));
    });
    if (!this.reportListSort.active || !this.reportListSort.direction) {
      return filtered;
    }
    return [...filtered].sort((left, right) => this.compareReportListItems(left, right));
  }

  protected get groupProductOnboardingReportPendingCount(): number {
    return this.groupProductOnboardingReportRows.filter((row) => row?.status === 'pending').length;
  }

  protected get groupProductOnboardingReportReadyCount(): number {
    return this.groupProductOnboardingReportRows.filter((row) => row?.status === 'ready').length;
  }

  protected get filteredGroupProductOnboardingReportRows(): CompanyGroupProductOnboardingRow[] {
    if (this.selectedGroupProductOnboardingReportStatusFilter === 'all') {
      return this.groupProductOnboardingReportRows;
    }
    return this.groupProductOnboardingReportRows.filter(
      (row) => row?.status === this.selectedGroupProductOnboardingReportStatusFilter
    );
  }

  protected get groupProductOnboardingReportVisibleRowCountLabel(): string {
    const visibleCount = this.filteredGroupProductOnboardingReportRows.length;
    const totalCount = this.groupProductOnboardingReportRows.length;
    if (this.selectedGroupProductOnboardingReportStatusFilter === 'all' || visibleCount === totalCount) {
      return `${visibleCount} rows`;
    }
    return `${visibleCount} of ${totalCount} rows`;
  }

  protected isGroupProductOnboardingStatusFilterSelected(
    filter: GroupProductOnboardingReportStatusFilter,
  ): boolean {
    return this.selectedGroupProductOnboardingReportStatusFilter === filter;
  }

  protected setGroupProductOnboardingStatusFilter(
    filter: GroupProductOnboardingReportStatusFilter,
    event?: Event,
  ): void {
    event?.stopPropagation();
    this.selectedGroupProductOnboardingReportStatusFilter = filter;
  }

  protected exportGroupProductOnboardingReport(): void {
    if (!this.filteredGroupProductOnboardingReportRows.length) {
      return;
    }

    void this.exportToCsvService.export(
      'group-product-onboarding-report',
      this.filteredGroupProductOnboardingReportRows.map((row) => ({
        companyName: row.companyName || '',
        providerName: row.providerName || '',
        planName: row.planName || '',
        status: row.status || '',
        requestedByName: row.userName || row.requestedBy?.name || '',
        requestedByEmail: row.userEmail || row.requestedBy?.email || '',
        requestedByPhone: row.userPhone || row.requestedBy?.phone || '',
        requestedAt: row.requestedAt || '',
        readyByName: row.readyBy?.name || '',
        readyByEmail: row.readyBy?.email || '',
        readyAt: row.readyAt || '',
      }))
    );
  }

  protected getGroupProductOnboardingStatusLabel(row: CompanyGroupProductOnboardingRow): string {
    return row?.status === 'pending' ? 'Awaiting Confirmation' : row?.status === 'ready' ? 'Ready' : 'Not Requested';
  }

  protected canMarkGroupProductOnboardingReady(row: CompanyGroupProductOnboardingRow): boolean {
    return row?.status === 'pending'
      && !!row?.companyId
      && !!row?.requestedVendorProductId;
  }

  protected isMarkingGroupProductOnboardingReady(row: CompanyGroupProductOnboardingRow): boolean {
    return this.pendingGroupProductOnboardingReadyKeys.has(this.getGroupProductOnboardingReadyKey(row));
  }

  protected canDeleteGroupProductOnboardingRequest(row: CompanyGroupProductOnboardingRow): boolean {
    return !!row?.companyId && !!row?.requestedVendorProductId;
  }

  protected isDeletingGroupProductOnboardingRequest(row: CompanyGroupProductOnboardingRow): boolean {
    return this.pendingGroupProductOnboardingDeleteKeys.has(this.getGroupProductOnboardingReadyKey(row));
  }

  protected async markGroupProductOnboardingReadyFromReport(row: CompanyGroupProductOnboardingRow): Promise<void> {
    if (!this.canMarkGroupProductOnboardingReady(row)) {
      return;
    }

    const rowKey = this.getGroupProductOnboardingReadyKey(row);
    if (this.pendingGroupProductOnboardingReadyKeys.has(rowKey)) {
      return;
    }

    const dialogRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogModel, boolean>(ConfirmDialogComponent, {
      data: new ConfirmDialogModel(
        'Mark Product Approval Ready',
        `Mark <strong>${row.providerName || 'this provider'} - ${row.planName || 'this plan'}</strong> ready for <strong>${row.companyName || 'this company'}</strong>?`,
        false,
        'Mark Ready',
        'Cancel',
      ),
      autoFocus: false,
    });

    const confirmed = await dialogRef.afterClosed().toPromise();
    if (!confirmed) {
      return;
    }

    this.pendingGroupProductOnboardingReadyKeys.add(rowKey);
    try {
      const updatedRow = await this.teamCompaniesService.markGroupProductOnboardingReady(
        row.companyId,
        row.requestedVendorProductId,
      );
      this.groupProductOnboardingReportRows = this.groupProductOnboardingReportRows.map((existingRow) => {
        if (this.getGroupProductOnboardingReadyKey(existingRow) !== rowKey) {
          return existingRow;
        }

        return {
          ...existingRow,
          ...updatedRow,
          companyName: existingRow.companyName || updatedRow.companyName,
          userName: existingRow.userName || updatedRow.userName,
          userEmail: existingRow.userEmail || updatedRow.userEmail,
          userPhone: existingRow.userPhone || updatedRow.userPhone,
          requestedBy: {
            ...(existingRow.requestedBy || {}),
            ...(updatedRow.requestedBy || {}),
          },
          readyBy: {
            ...(existingRow.readyBy || {}),
            ...(updatedRow.readyBy || {}),
          },
        };
      });
      this.snackBar.open(
        `${row.providerName || 'Product'} - ${row.planName || ''} marked ready.`,
        'Dismiss',
        { duration: 3000 },
      );
    } catch (error: any) {
      this.presentError(error, 'Unable to mark this product approval ready.');
    } finally {
      this.pendingGroupProductOnboardingReadyKeys.delete(rowKey);
    }
  }

  protected async deleteGroupProductOnboardingRequestFromReport(row: CompanyGroupProductOnboardingRow): Promise<void> {
    if (!this.canDeleteGroupProductOnboardingRequest(row)) {
      return;
    }

    const rowKey = this.getGroupProductOnboardingReadyKey(row);
    if (this.pendingGroupProductOnboardingDeleteKeys.has(rowKey)) {
      return;
    }

    const dialogRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogModel, boolean>(ConfirmDialogComponent, {
      data: new ConfirmDialogModel(
        'Delete Product Approval Request',
        this.getDeleteGroupProductOnboardingRequestConfirmationCopy(row),
        false,
        'Delete Request',
        'Cancel',
      ),
      autoFocus: false,
    });

    const confirmed = await dialogRef.afterClosed().toPromise();
    if (!confirmed) {
      return;
    }

    this.pendingGroupProductOnboardingDeleteKeys.add(rowKey);
    try {
      await this.teamCompaniesService.deleteGroupProductOnboardingRequest(
        row.companyId,
        row.requestedVendorProductId,
      );
      this.groupProductOnboardingReportRows = this.groupProductOnboardingReportRows.filter(
        (existingRow) => this.getGroupProductOnboardingReadyKey(existingRow) !== rowKey,
      );
      this.snackBar.open(
        `${row.providerName || 'Product'} - ${row.planName || ''} request deleted.`,
        'Dismiss',
        { duration: 3000 },
      );
    } catch (error: any) {
      this.presentError(error, 'Unable to delete this product approval request.');
    } finally {
      this.pendingGroupProductOnboardingDeleteKeys.delete(rowKey);
    }
  }

  protected onReportListSortChange(sort: Sort): void {
    this.reportListSort.active = (sort.active || '') as ReportListSortColumn | '';
    this.reportListSort.direction = sort.direction;
  }

  protected get groupedFields(): Array<{ group: string; fields: ScheduledReportFieldDefinition[] }> {
    const baseEntity = `${this.reportForm.get('baseEntity')?.value || 'users'}`;
    if (this.groupedFieldsCacheSource === this.fieldCatalog && this.groupedFieldsCacheBaseEntity === baseEntity) {
      return this.groupedFieldsCache;
    }

    const grouped = new Map<string, ScheduledReportFieldDefinition[]>();
    this.fieldCatalog
      .filter((field) => !field.supportedBaseEntities || field.supportedBaseEntities.includes(baseEntity))
      .forEach((field) => {
        if (!grouped.has(field.group)) {
          grouped.set(field.group, []);
        }
        grouped.get(field.group)?.push(field);
      });

    this.groupedFieldsCache = Array.from(grouped.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([group, fields]) => ({
        group,
        fields: fields.sort((left, right) => left.label.localeCompare(right.label)),
      }));
    this.groupedFieldsCacheBaseEntity = baseEntity;
    this.groupedFieldsCacheSource = this.fieldCatalog;
    return this.groupedFieldsCache;
  }

  protected get datasetOptions(): DatasetOption[] {
    if (this.datasetOptionsCacheSource === this.baseEntities) {
      return this.datasetOptionsCache;
    }

    const advertisedBaseEntityByKey = new Map(
      this.baseEntities
        .filter((baseEntity) => `${baseEntity?.key || ''}`.trim())
        .map((baseEntity) => [`${baseEntity.key}`.trim(), baseEntity]),
    );
    const knownKeys = new Set(this.knownDatasetOptions.map((dataset) => dataset.key));

    const options = this.knownDatasetOptions.map((dataset) => {
      const baseEntity = advertisedBaseEntityByKey.get(dataset.key);
      return {
        key: dataset.key,
        label: baseEntity?.label || dataset.label,
        enabled: true,
        note: baseEntity?.description || dataset.description,
      };
    });

    this.baseEntities.forEach((baseEntity) => {
      const key = `${baseEntity?.key || ''}`.trim();
      if (!key || knownKeys.has(key)) {
        return;
      }

      options.push({
        key,
        label: baseEntity.label || key,
        enabled: true,
        note: baseEntity.description || 'Available now.',
      });
    });

    this.datasetOptionsCache = options.sort((left, right) => left.label.localeCompare(right.label, undefined, {
      sensitivity: 'base',
    }));
    this.datasetOptionsCacheSource = this.baseEntities;
    return this.datasetOptionsCache;
  }

  protected get columnsArray(): UntypedFormArray {
    return this.reportForm.get('columns') as UntypedFormArray;
  }

  protected get templateColumnsArray(): UntypedFormArray {
    return this.reportForm.get('templateColumns') as UntypedFormArray;
  }

  protected get groupByArray(): UntypedFormArray {
    return this.reportForm.get('aggregation.groupBy') as UntypedFormArray;
  }

  protected get aggregationGroup(): UntypedFormGroup {
    return this.reportForm.get('aggregation') as UntypedFormGroup;
  }

  protected get aggregationCountDistinctArgsGroup(): UntypedFormGroup {
    return this.aggregationGroup.get('countDistinctArgs') as UntypedFormGroup;
  }

  protected get templateArgsGroup(): UntypedFormGroup {
    return this.reportForm.get('templateArgs') as UntypedFormGroup;
  }

  protected get customAudienceGroup(): UntypedFormGroup {
    return this.reportForm.get('customAudience') as UntypedFormGroup;
  }

  protected get temporalGroup(): UntypedFormGroup {
    return this.reportForm.get('temporal') as UntypedFormGroup;
  }

  protected isSubscriptionCoverageDataset(): boolean {
    return !this.isTemplateReport && `${this.reportForm.get('baseEntity')?.value || ''}` === 'vendorSubscriptionCoverage';
  }

  protected isProductEnrollmentEventsDataset(): boolean {
    return !this.isTemplateReport && `${this.reportForm.get('baseEntity')?.value || ''}` === 'usersVendorProducts';
  }

  protected get deliveryConfigGroup(): UntypedFormGroup {
    return this.reportForm.get('deliveryConfig') as UntypedFormGroup;
  }

  protected get scheduleConfigGroup(): UntypedFormGroup {
    return this.reportForm.get('scheduleConfig') as UntypedFormGroup;
  }

  protected get filtersArray(): UntypedFormArray {
    return this.reportForm.get('filters') as UntypedFormArray;
  }

  protected get sortArray(): UntypedFormArray {
    return this.reportForm.get('sort') as UntypedFormArray;
  }

  protected get selectedTemplate(): ScheduledReportTemplate | null {
    const key = `${this.reportForm.get('templateKey')?.value || ''}`;
    return this.templates.find((template) => template.key === key) || null;
  }

  protected get selectedTemplateSupportsColumnCustomization(): boolean {
    return !!(this.selectedTemplate?.supportsColumnCustomization && this.getSortedTemplateDefaultColumns(this.selectedTemplate).length);
  }

  protected get selectedTemplateSupportsSortCustomization(): boolean {
    return !!(this.selectedTemplate?.supportsSortCustomization && this.getSortedTemplateDefaultSort(this.selectedTemplate).length);
  }

  protected get selectedTemplateSupportsEnrollmentStatusFilter(): boolean {
    return this.selectedTemplate?.key === 'partner_subscription_roster';
  }

  protected get vendorOptions(): VendorOption[] {
    const byVendor = new Map<string, VendorOption>();
    this.productOptions.forEach((product) => {
      if (!product.vendorSlug || byVendor.has(product.vendorSlug)) {
        return;
      }
      byVendor.set(product.vendorSlug, {
        value: product.vendorSlug,
        label: product.vendorName || product.vendorSlug,
      });
    });
    return Array.from(byVendor.values()).sort((left, right) => left.label.localeCompare(right.label));
  }

  protected get selectedTemplateNeedsVendorSelection(): boolean {
    return !!(this.selectedTemplate?.templateArgsSchema?.vendorSelections || this.selectedTemplate?.templateArgsSchema?.vendorSlug);
  }

  protected get selectedTemplateNeedsProductSelection(): boolean {
    return !!(this.selectedTemplate?.templateArgsSchema?.vendorSelections || this.selectedTemplate?.templateArgsSchema?.productSlugs);
  }

  protected get allTemplateVendorOptionsSelected(): boolean {
    return this.vendorOptions.length > 0
      && this.getSelectedTemplateVendorSlugs().length === this.vendorOptions.length;
  }

  protected get latestArtifactUrl(): string {
    const run = this.reportRuns.find((item) => item.status === 'success' && item.artifactUrl);
    return run?.artifactUrl || '';
  }

  protected get isWeeklySchedule(): boolean {
    return this.reportForm.get('scheduleType')?.value === 'weekly';
  }

  protected get isMonthlySchedule(): boolean {
    return this.reportForm.get('scheduleType')?.value === 'monthly';
  }

  protected get isRollingWindow(): boolean {
    return !this.isTeamMembersRuntimeSnapshot
      && this.reportForm.get('scheduleConfig.windowMode')?.value === 'rolling_days';
  }

  protected get isTeamMembersRuntimeSnapshot(): boolean {
    return !this.isTemplateReport
      && `${this.reportForm.get('baseEntity')?.value || ''}` === 'teamusers'
      && `${this.temporalGroup.get('asOfMode')?.value || 'run_time'}` === 'run_time';
  }

  protected get isTemplateReport(): boolean {
    return this.reportForm.get('reportType')?.value === 'template';
  }

  protected get isAggregatedCustomReport(): boolean {
    return !this.isTemplateReport && this.reportForm.get('resultMode')?.value === 'aggregated';
  }

  protected get fileNamePreview(): string {
    const pattern = `${this.reportForm.get('fileNamePattern')?.value || ''}`.trim()
      || (this.selectedTemplate?.defaultFileNamePattern || this.getDefaultFileNamePattern());
    const outputFormat = `${this.reportForm.get('outputFormat')?.value || 'csv'}`;
    const runContext = this.preview?.runContext || this.detailPreview?.runContext || this.getFallbackRunContext();
    const runDate = new Date(runContext.asOfAt || new Date().toISOString());
    const periodStart = new Date(runContext.windowStart || new Date().toISOString());
    const periodEnd = new Date(runContext.windowEnd || new Date().toISOString());

    return pattern.replace(/\{\{([^}:]+)(?::([^}]+))?\}\}/g, (_, key: string, format: string) => {
      if (key === 'extension') {
        return outputFormat;
      }
      const date = key === 'periodStart'
        ? periodStart
        : key === 'periodEnd'
          ? periodEnd
          : runDate;
      return this.formatPatternDate(date, format || 'YYYYMMDD');
    });
  }

  protected get currentPermissions(): PermissionSet {
    const source = this.detailReport as any;
    if (source?.permissions) {
      return {
        create: source.permissions.create !== false,
        edit: source.permissions.edit !== false,
        delete: source.permissions.delete !== false,
        pauseResume: source.permissions.pauseResume !== false,
        runManual: source.permissions.runManual !== false,
        rerunWithAsOf: source.permissions.rerunWithAsOf !== false,
        downloadArtifacts: source.permissions.downloadArtifacts !== false,
      };
    }
    return this.defaultPermissions;
  }

  protected get previewIsCurrent(): boolean {
    if (!this.preview || !this.previewSignature) {
      return false;
    }

    try {
      return this.previewSignature === this.buildPreviewSignature(this.buildPayload(this.currentEditorStatus));
    } catch {
      return false;
    }
  }

  protected get currentEditorStatus(): string {
    return `${this.reportForm.get('status')?.value || 'draft'}`;
  }

  protected get canActivate(): boolean {
    return this.currentPermissions.create
      && this.collectValidationMessages(true).length === 0
      && this.previewIsCurrent;
  }

  protected setListTab(tab: ReportTabType): void {
    this.activeListTab = tab;
  }

  protected async reloadReports(): Promise<void> {
    this.isListLoading = true;
    try {
      this.reports = await this.scheduledReportsService.listReports({ limit: 200, offset: 0 });
    } catch (error: any) {
      this.presentError(error, 'Unable to load scheduled reports.');
    } finally {
      this.isListLoading = false;
    }
  }

  protected startCreate(tab: ReportTabType): void {
    this.editorIncludeDemoUsers = false;
    this.detailIncludeDemoUsers = false;
    this.viewMode = 'create';
    this.activeListTab = tab;
    this.editingReportId = '';
    this.detailReport = null;
    this.reportRuns = [];
    this.selectedRun = null;
    this.detailPreview = null;
    this.preview = null;
    this.previewSignature = '';
    this.previewError = '';
    this.clearEditorSensitivePreviewState();
    this.clearDetailSensitivePreviewState();
    this.validationMessages = [];
    this.reportForm.reset({
      name: '',
      description: '',
      status: 'draft',
      reportType: tab,
      resultMode: 'detail',
      scopeType: 'global',
      scopeIds: [],
      templateKey: '',
      baseEntity: 'users',
      outputFormat: tab === 'template' ? (this.templates[0]?.defaultOutputFormat || 'csv') : 'csv',
      fileNamePattern: tab === 'template' ? (this.templates[0]?.defaultFileNamePattern || this.getDefaultFileNamePattern()) : this.getDefaultFileNamePattern(),
      showReportDates: false,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles',
      scheduleType: 'weekly',
      deliveryChannel: 'email',
      distinct: true,
      scheduleConfig: {
        time: '08:00',
        daysOfWeek: [1],
        dayOfMonth: 1,
        windowMode: 'previous_month',
        rollingDays: 7,
      },
      templateArgs: {
        vendorSlugs: [],
        vendorSlug: '',
        productSlugs: [],
        enrollmentStatusFilter: 'all',
        includeUsersWithoutCurrentTeamMembership: true,
      },
      customAudience: {
        includeUsersWithoutCurrentTeamMembership: true,
      },
      temporal: {
        asOfMode: 'run_time',
        subscriptionCoverageMode: 'window_overlap',
      },
      deliveryConfig: {
        host: '',
        port: 22,
        username: '',
        passwordSecretKey: '',
        privateKeySecretKey: '',
        folder: '',
        fileNamePrefix: '',
        pgpEnabled: false,
        publicKeySecretKey: '',
        toText: '',
        ccText: '',
        bccText: '',
        subjectTemplate: '',
        bodyTemplate: '',
      },
      aggregation: {
        countHeader: 'Count',
        countDistinctField: this.getDefaultCountDistinctFieldKey(),
      },
    }, { emitEvent: false });
    this.replaceAggregationCountDistinctArgsGroup(`${this.aggregationGroup.get('countDistinctField')?.value || ''}`);
    this.replaceFormArray(this.groupByArray, []);
    this.replaceFormArray(this.templateColumnsArray, []);
    this.replaceFormArray(this.columnsArray, []);
    this.replaceFormArray(this.filtersArray, []);
    this.replaceFormArray(this.sortArray, []);
    if (tab === 'template') {
      this.applyTemplateDefaults(this.selectedTemplate?.key || this.templates[0]?.key || '');
    }
    this.ensureScopeSelection();
    this.updateDynamicValidators();
  }

  protected async openReport(report: ScheduledReport): Promise<void> {
    this.detailIncludeDemoUsers = false;
    this.viewMode = 'detail';
    this.isDetailLoading = true;
    this.detailPreview = null;
    this.detailPreviewError = '';
    this.detailReportIsSensitive = false;
    this.clearDetailSensitivePreviewState();
    this.selectedRun = null;
    try {
      const [loadedReport, runs] = await Promise.all([
        this.scheduledReportsService.getReport(report.id),
        this.scheduledReportsService.listRuns(report.id, { limit: 100, offset: 0 }),
      ]);
      this.detailReport = loadedReport;
      this.reportRuns = runs;
      this.selectedRun = runs[0] || null;
      this.detailReportIsSensitive = this.isSensitiveReport(loadedReport);
      if (!this.detailReportIsSensitive) {
        try {
          const runtimeParameters = await this.promptRuntimeParameters(loadedReport);
          if (runtimeParameters !== null) {
            const overrides = runtimeParameters ? { runtimeParameters } : {};
            this.detailPreview = await this.scheduledReportsService.previewSaved(report.id, overrides, this.previewRowLimit);
          }
        } catch (previewError: any) {
          this.detailPreview = null;
          this.detailPreviewError = this.getErrorMessage(previewError, 'Preview failed.');
        }
      }
    } catch (error: any) {
      this.presentError(error, 'Unable to load scheduled report details.');
      this.viewMode = 'list';
    } finally {
      this.isDetailLoading = false;
    }
  }

  protected editCurrentReport(): void {
    if (!this.detailReport) {
      return;
    }

    if (this.detailSensitivePreviewMode === 'revealed') {
      this.maskDetailSsns();
    }
    this.loadReportIntoEditor(this.detailReport, false);
  }

  protected backToReportList(): void {
    this.viewMode = 'list';
    this.detailPreview = null;
    this.detailPreviewError = '';
    this.clearDetailSensitivePreviewState();
  }

  protected duplicateReport(report: ScheduledReport): void {
    this.loadReportIntoEditor(report, true);
  }

  protected async confirmDeleteReport(report: ScheduledReport, event?: Event): Promise<void> {
    event?.stopPropagation();
    if (!this.currentPermissions.delete || !report?.id) {
      return;
    }

    const dialogRef = this.dialog.open<ConfirmDialogComponent, ConfirmDialogModel, boolean>(ConfirmDialogComponent, {
      data: new ConfirmDialogModel(
        'Archive Scheduled Report',
        `Archive scheduled report <strong>${report.name}</strong>? It will be removed from the default list.`,
        false,
        'Archive',
        'Cancel',
      ),
      autoFocus: false,
    });

    const confirmed = await dialogRef.afterClosed().toPromise();
    if (!confirmed) {
      return;
    }

    try {
      await this.scheduledReportsService.deleteReport(report.id);
      this.reports = this.reports.filter((item) => item.id !== report.id);
      if (this.detailReport?.id === report.id) {
        this.detailReport = null;
        this.reportRuns = [];
        this.selectedRun = null;
        this.detailPreview = null;
        this.viewMode = 'list';
      }
      if (this.editingReportId === report.id) {
        this.editingReportId = '';
        this.viewMode = 'list';
      }
      this.preview = null;
      this.previewSignature = '';
      this.previewError = '';
      this.clearEditorSensitivePreviewState();
      this.snackBar.open('Scheduled report archived.', 'Dismiss', { duration: 3000 });
    } catch (error: any) {
      this.presentError(error, 'Unable to archive the scheduled report.');
    }
  }

  protected addColumn(field?: ScheduledReportFieldDefinition): void {
    const selectedField = field || this.getDefaultFieldForColumns();
    if (!selectedField) {
      return;
    }

    this.columnsArray.push(this.buildColumnGroup(selectedField));
  }

  protected removeColumn(index: number): void {
    this.columnsArray.removeAt(index);
  }

  protected addGroupBy(field?: ScheduledReportFieldDefinition): void {
    const selectedField = field || this.getDefaultFieldForColumns();
    if (!selectedField) {
      return;
    }

    this.groupByArray.push(this.buildAggregationGroupByControl(selectedField));
  }

  protected removeGroupBy(index: number): void {
    this.groupByArray.removeAt(index);
  }

  protected addFilter(field?: ScheduledReportFieldDefinition): void {
    const selectedField = field || this.getDefaultFieldForColumns();
    if (!selectedField) {
      return;
    }
    this.filtersArray.push(this.buildFilterGroup(selectedField));
  }

  protected removeFilter(index: number): void {
    this.filtersArray.removeAt(index);
  }

  protected addSort(field?: ScheduledReportFieldDefinition): void {
    const selectedField = field || this.getSortableFields()[0];
    if (!selectedField) {
      return;
    }
    this.sortArray.push(this.buildSortGroup({
      field: selectedField.key,
      direction: 'asc',
    }));
  }

  protected removeSort(index: number): void {
    this.sortArray.removeAt(index);
  }

  protected onSortDrop(event: CdkDragDrop<AbstractControl[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    moveItemInArray(this.sortArray.controls, event.previousIndex, event.currentIndex);
    this.sortArray.updateValueAndValidity();
  }

  protected onColumnDrop(event: CdkDragDrop<AbstractControl[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    moveItemInArray(this.columnsArray.controls, event.previousIndex, event.currentIndex);
    this.columnsArray.updateValueAndValidity();
  }

  protected onTemplateColumnDrop(event: CdkDragDrop<AbstractControl[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }
    moveItemInArray(this.templateColumnsArray.controls, event.previousIndex, event.currentIndex);
    this.templateColumnsArray.updateValueAndValidity();
  }

  protected async changeEditorDemoPreview(include: boolean): Promise<void> {
    this.editorIncludeDemoUsers = include;
    this.preview = null;
    this.previewSignature = '';
    this.clearEditorSensitivePreviewState();
    await this.previewDraft();
  }

  protected async changeDetailDemoPreview(include: boolean): Promise<void> {
    this.detailIncludeDemoUsers = include;
    this.detailPreview = null;
    this.clearDetailSensitivePreviewState();
    if (this.detailReportIsSensitive) {
      await this.previewDetailSensitiveMasked();
      return;
    }
    const report = this.detailReport;
    if (!report) {
      return;
    }
    this.isDetailPreviewLoading = true;
    this.detailPreviewError = '';
    try {
      const runtimeParameters = await this.promptRuntimeParameters(report);
      if (runtimeParameters === null) {
        return;
      }
      this.detailPreview = await this.scheduledReportsService.previewSaved(report.id, {
        ...(runtimeParameters ? { runtimeParameters } : {}),
        includeDemoUsers: include,
      }, this.previewRowLimit);
    } catch (error: any) {
      this.detailPreviewError = this.getErrorMessage(error, 'Preview failed.');
    } finally {
      this.isDetailPreviewLoading = false;
    }
  }

  protected async previewDraft(): Promise<void> {
    this.validationMessages = this.collectValidationMessages(false);
    if (this.validationMessages.length > 0) {
      this.markEditorTouched();
      return;
    }

    let payload: ScheduledReportPayload;
    try {
      payload = this.buildPayload(this.currentEditorStatus);
    } catch (error: any) {
      this.validationMessages = [error.message || 'Review the report configuration and try again.'];
      return;
    }

    const runtimeParameters = await this.promptRuntimeParameters(payload);
    if (runtimeParameters === null) {
      return;
    }

    const isSensitive = this.isSensitiveReport(payload);

    this.isPreviewLoading = true;
    this.previewError = '';
    try {
      if (isSensitive) {
        this.preview = await this.scheduledReportsService.previewSensitiveDraft(
          payload,
          null,
          'masked',
          this.sensitivePreviewRowLimit,
          runtimeParameters || undefined,
          this.editorIncludeDemoUsers,
        );
        this.editorSensitiveRuntimeParameters = runtimeParameters || undefined;
        this.editorMaskedSensitivePreview = this.preview;
        this.editorSensitivePreviewMode = 'masked';
      } else {
        this.preview = await this.scheduledReportsService.previewDraft(
          payload,
          this.previewRowLimit,
          runtimeParameters || undefined,
          this.editorIncludeDemoUsers,
        );
        this.clearEditorSensitivePreviewState();
      }
      this.previewSignature = this.buildPreviewSignature(payload);
      this.snackBar.open('Preview updated.', 'Dismiss', { duration: 3000 });
    } catch (error: any) {
      this.preview = null;
      this.previewSignature = '';
      this.clearEditorSensitivePreviewState();
      this.previewError = this.getErrorMessage(error, 'Preview failed.');
      this.presentError(error, this.previewError);
    } finally {
      this.isPreviewLoading = false;
    }
  }

  protected async revealEditorFullSsns(): Promise<void> {
    let payload: ScheduledReportPayload;
    try {
      payload = this.buildPayload(this.currentEditorStatus);
    } catch (error: any) {
      this.previewError = error.message || 'Review the report configuration and try again.';
      return;
    }

    if (!this.isSensitiveReport(payload) || this.previewSignature !== this.buildPreviewSignature(payload)) {
      this.previewError = 'Run a new masked preview before revealing full SSNs.';
      return;
    }

    const sensitiveAccess = await this.promptSensitivePreviewAccess(payload.name);
    if (!sensitiveAccess) {
      return;
    }

    this.isPreviewLoading = true;
    this.previewError = '';
    try {
      this.preview = await this.scheduledReportsService.previewSensitiveDraft(
        payload,
        sensitiveAccess,
        'revealed',
        this.sensitivePreviewRowLimit,
        this.editorSensitiveRuntimeParameters,
        this.editorIncludeDemoUsers,
      );
      this.editorSensitiveAccess = sensitiveAccess;
      this.editorSensitivePreviewMode = 'revealed';
      this.snackBar.open('Full SSNs revealed for this audited preview.', 'Dismiss', { duration: 4000 });
    } catch (error: any) {
      this.previewError = this.getErrorMessage(error, 'Unable to reveal full SSNs.');
      this.presentError(error, this.previewError);
    } finally {
      this.isPreviewLoading = false;
    }
  }

  protected maskEditorSsns(): void {
    if (!this.editorMaskedSensitivePreview) {
      return;
    }
    this.preview = this.editorMaskedSensitivePreview;
    this.editorSensitivePreviewMode = 'masked';
  }

  protected async previewDetailSensitiveMasked(): Promise<void> {
    const report = this.detailReport;
    if (!report || !this.detailReportIsSensitive) {
      return;
    }

    const runtimeParameters = await this.promptRuntimeParameters(report);
    if (runtimeParameters === null) {
      return;
    }
    const overrides = {
      ...(runtimeParameters ? { runtimeParameters } : {}),
      ...(this.detailIncludeDemoUsers ? { includeDemoUsers: true } : {}),
    };
    this.isDetailPreviewLoading = true;
    this.detailPreviewError = '';
    try {
      this.detailPreview = await this.scheduledReportsService.previewSensitiveSaved(
        report.id,
        null,
        'masked',
        overrides,
        this.sensitivePreviewRowLimit,
      );
      this.detailSensitivePreviewOverrides = overrides;
      this.detailMaskedSensitivePreview = this.detailPreview;
      this.detailSensitivePreviewMode = 'masked';
      this.snackBar.open('Sensitive preview updated with SSNs masked.', 'Dismiss', { duration: 3000 });
    } catch (error: any) {
      this.detailPreview = null;
      this.clearDetailSensitivePreviewState();
      this.detailPreviewError = this.getErrorMessage(error, 'Preview failed.');
      this.presentError(error, this.detailPreviewError);
    } finally {
      this.isDetailPreviewLoading = false;
    }
  }

  protected async revealDetailFullSsns(): Promise<void> {
    const report = this.detailReport;
    if (!report || !this.detailReportIsSensitive || !this.detailMaskedSensitivePreview) {
      return;
    }

    const sensitiveAccess = await this.promptSensitivePreviewAccess(report.name);
    if (!sensitiveAccess) {
      return;
    }

    this.isDetailPreviewLoading = true;
    this.detailPreviewError = '';
    try {
      this.detailPreview = await this.scheduledReportsService.previewSensitiveSaved(
        report.id,
        sensitiveAccess,
        'revealed',
        this.detailSensitivePreviewOverrides,
        this.sensitivePreviewRowLimit,
      );
      this.detailSensitiveAccess = sensitiveAccess;
      this.detailSensitivePreviewMode = 'revealed';
      this.snackBar.open('Full SSNs revealed for this audited preview.', 'Dismiss', { duration: 4000 });
    } catch (error: any) {
      this.detailPreviewError = this.getErrorMessage(error, 'Unable to reveal full SSNs.');
      this.presentError(error, this.detailPreviewError);
    } finally {
      this.isDetailPreviewLoading = false;
    }
  }

  protected maskDetailSsns(): void {
    if (!this.detailMaskedSensitivePreview) {
      return;
    }
    this.detailPreview = this.detailMaskedSensitivePreview;
    this.detailSensitivePreviewMode = 'masked';
  }

  protected async saveDraft(): Promise<void> {
    await this.persistReport('draft');
  }

  protected async activateReport(): Promise<void> {
    await this.persistReport('active');
  }

  protected async sendNowFromEditor(): Promise<void> {
    if (!this.currentPermissions.runManual) {
      return;
    }

    const status = `${this.reportForm.get('status')?.value || 'draft'}` as 'draft' | 'active' | 'paused';
    const saved = await this.persistReport(status, false);
    if (!saved) {
      return;
    }

    await this.runNow(saved);
  }

  protected cancelEditor(): void {
    this.viewMode = this.detailReport && this.editingReportId ? 'detail' : 'list';
    this.preview = null;
    this.previewError = '';
    this.previewSignature = '';
    this.clearEditorSensitivePreviewState();
    this.validationMessages = [];
  }

  protected async togglePauseResume(report: ScheduledReport): Promise<void> {
    if (!this.currentPermissions.pauseResume) {
      return;
    }

    const nextStatus = report.status === 'active' ? 'paused' : 'active';
    try {
      const updated = await this.scheduledReportsService.updateReport(report.id, { status: nextStatus });
      this.replaceReport(updated);
      if (this.detailReport?.id === updated.id) {
        this.detailReport = updated;
      }
      this.snackBar.open(nextStatus === 'paused' ? 'Report paused.' : 'Report resumed.', 'Dismiss', { duration: 3000 });
    } catch (error: any) {
      this.presentError(error, 'Unable to update the report status.');
    }
  }

  protected async runNow(report: ScheduledReport): Promise<void> {
    if (!this.currentPermissions.runManual) {
      return;
    }

    try {
      const runtimeParameters = await this.promptRuntimeParameters(report);
      if (runtimeParameters === null) {
        return;
      }
      let sensitiveAccess: ScheduledReportSensitiveAccess | undefined;
      if (this.isSensitiveReport(report)) {
        sensitiveAccess = this.getSensitiveAccessForRun(report) || undefined;
        if (!sensitiveAccess) {
          sensitiveAccess = await this.promptSensitivePreviewAccess(report.name) || undefined;
          if (!sensitiveAccess) {
            return;
          }
        }
      }
      const result = await this.scheduledReportsService.runNow(
        report.id,
        runtimeParameters || undefined,
        sensitiveAccess,
      );
      this.snackBar.open(
        result.status === 'success' ? `Report ran successfully with ${result.rowsGenerated || 0} rows.` : 'Report run completed.',
        'Dismiss',
        { duration: 4000 },
      );
      await this.reloadReports();
      if (this.detailReport?.id === report.id) {
        await this.openReport(report);
      }
    } catch (error: any) {
      this.presentError(error, 'Unable to run the report now.');
    }
  }

  private async promptRuntimeParameters(
    report: ScheduledReport | ScheduledReportPayload | null,
  ): Promise<Record<string, any> | null | undefined> {
    const runtimeFilters = this.getRuntimeFilters(report);
    if (!runtimeFilters.length) {
      return undefined;
    }

    const dialogRef = this.dialog.open<
      PayrollDashboardReportParametersDialogComponent,
      { reportName: string; filters: ScheduledReportDefinitionFilter[]; fields: ScheduledReportFieldDefinition[]; valueOptions: Record<string, ScheduledReportValueOption[]> },
      PayrollDashboardReportParameterDialogResult
    >(PayrollDashboardReportParametersDialogComponent, {
      width: '620px',
      data: {
        reportName: report?.name || 'Report',
        filters: runtimeFilters,
        fields: this.fieldCatalog,
        valueOptions: this.getReportValueOptionMap(),
      },
      autoFocus: false,
    });

    const result = await dialogRef.afterClosed().toPromise();
    return result || null;
  }

  private async promptSensitivePreviewAccess(
    reportName: string,
  ): Promise<ScheduledReportSensitiveAccess | null> {
    const dialogRef = this.dialog.open<
      PayrollSensitiveReportPreviewDialogComponent,
      {
        reportName: string;
        businessPurposes: ScheduledReportBusinessPurpose[];
      },
      PayrollSensitiveReportPreviewDialogResult
    >(PayrollSensitiveReportPreviewDialogComponent, {
      width: '640px',
      data: {
        reportName,
        businessPurposes: this.sensitivePreviewBusinessPurposes,
      },
      autoFocus: false,
      disableClose: true,
    });

    const result = await dialogRef.afterClosed().toPromise();
    return result?.sensitiveAccess || null;
  }

  private isSensitiveReport(report: ScheduledReport | ScheduledReportPayload): boolean {
    const definition = report.definitionJson || { baseEntity: '', columns: [] };
    const fieldReferences: Array<Partial<ScheduledReportDefinitionColumn> & { field?: string }> = [];
    const addReference = (reference: any): void => {
      if (!reference || typeof reference !== 'object') {
        return;
      }
      if (`${reference.field || ''}`.trim() || this.isDecryptionTransform(reference.transform)) {
        fieldReferences.push(reference);
      }
    };

    (definition.columns || []).forEach((column) => {
      addReference(column);
      Object.values(column?.sources || {}).forEach(addReference);
    });
    (definition.filters || []).forEach(addReference);
    (definition.sort || []).forEach(addReference);
    (definition.aggregation?.groupBy || []).forEach(addReference);
    addReference(definition.aggregation?.countDistinct);

    if (`${report.reportType || ''}` === 'template') {
      const template = this.getTemplateByKey(report.templateKey);
      const columnOverrides = (report.templateArgs?.columnOverrides || []) as ScheduledReportTemplateColumnOverride[];
      this.getOrderedTemplateColumns(template, columnOverrides).forEach(addReference);
      (template?.defaultFilters || []).forEach(addReference);
      (template?.defaultSort || []).forEach(addReference);
    }

    return fieldReferences.some((reference) => {
      const field = this.getField(`${reference.field || ''}`);
      const classifications = this.normalizeDataClassifications(field?.dataClassification);
      const transform = reference.transform || field?.defaultTransform || null;
      return classifications.includes('full_ssn')
        || (this.isDecryptionTransform(transform) && classifications.length === 0);
    });
  }

  private normalizeDataClassifications(value?: string | string[] | null): string[] {
    return (Array.isArray(value) ? value : [value])
      .filter((classification): classification is string => typeof classification === 'string')
      .map((classification) => classification.trim())
      .filter(Boolean);
  }

  private isDecryptionTransform(value?: ScheduledReportOutputTransform | string | null): boolean {
    return ['decrypt', 'decrypt_digits'].includes(`${value || ''}`.trim());
  }

  private clearEditorSensitivePreviewState(): void {
    this.editorSensitiveRuntimeParameters = undefined;
    this.editorSensitiveAccess = null;
    this.editorMaskedSensitivePreview = null;
    this.editorSensitivePreviewMode = null;
  }

  private clearDetailSensitivePreviewState(): void {
    this.detailSensitivePreviewOverrides = undefined;
    this.detailSensitiveAccess = null;
    this.detailMaskedSensitivePreview = null;
    this.detailSensitivePreviewMode = null;
  }

  private getSensitiveAccessForRun(report: ScheduledReport): ScheduledReportSensitiveAccess | null {
    if (this.editingReportId === report.id && this.editorSensitiveAccess) {
      return this.editorSensitiveAccess;
    }
    if (this.detailReport?.id === report.id && this.detailSensitiveAccess) {
      return this.detailSensitiveAccess;
    }
    return null;
  }

  private getRuntimeFilters(
    report: ScheduledReport | ScheduledReportPayload | null,
  ): ScheduledReportDefinitionFilter[] {
    return (report?.definitionJson?.filters || [])
      .filter((filter) => filter?.valueMode === 'runtime' && filter.parameterKey);
  }

  protected async rerunWithAsOf(report: ScheduledReport, run?: ScheduledReportRun | null): Promise<void> {
    if (!this.currentPermissions.rerunWithAsOf) {
      return;
    }

    const isSensitive = this.isSensitiveReport(report);
    const dialogRef = this.dialog.open(PayrollReportRerunDialogComponent, {
      width: '720px',
      data: {
        reportName: report.name,
        timezone: report.timezone,
        windowStart: run?.windowStart || this.detailPreview?.runContext?.windowStart || null,
        windowEnd: run?.windowEnd || this.detailPreview?.runContext?.windowEnd || null,
        asOfAt: run?.asOfAt || this.detailPreview?.runContext?.asOfAt || null,
        deliveryChannel: report.deliveryChannel,
        emailDeliveryConfig: report.deliveryChannel === 'email'
          ? report.deliveryConfig as ScheduledReportEmailDeliveryConfig
          : null,
        allowDeliveryOverride: true,
        isSensitive,
      },
    });

    const result: PayrollReportRerunDialogResult | undefined = await dialogRef.afterClosed().toPromise();
    if (!result) {
      return;
    }

    try {
      await this.scheduledReportsService.rerun(report.id, result);
      this.snackBar.open('Rerun queued successfully.', 'Dismiss', { duration: 3000 });
      await this.reloadReports();
      if (this.detailReport?.id === report.id) {
        await this.openReport(report);
      }
    } catch (error: any) {
      this.presentError(error, 'Unable to rerun the report.');
    }
  }

  protected retryRun(run: ScheduledReportRun): void {
    if (!this.detailReport) {
      return;
    }
    void this.rerunWithAsOf(this.detailReport, run);
  }

  protected downloadArtifact(url: string | null | undefined): void {
    if (!url || !this.currentPermissions.downloadArtifacts) {
      return;
    }
    window.open(url, '_blank', 'noopener');
  }

  protected selectRun(run: ScheduledReportRun): void {
    this.selectedRun = run;
  }

  protected getScopeOptions(scopeType: string): ScopeOption[] {
    switch (scopeType) {
      case 'partner':
        return this.partners;
      case 'company':
        return this.companies;
      case 'team':
        return this.teams;
      default:
        return [];
    }
  }

  protected getSelectedScopeIds(scopeType?: string): string[] {
    const rawScopeIds = this.reportForm.get('scopeIds')?.value;
    const scopeIds = Array.isArray(rawScopeIds) ? rawScopeIds : (rawScopeIds ? [rawScopeIds] : []);
    if ((scopeType || this.reportForm.get('scopeType')?.value) === 'global') {
      return [];
    }
    return scopeIds.map((value) => `${value || ''}`.trim()).filter(Boolean);
  }

  protected getScopeLabel(report: ScheduledReport | null): string {
    if (!report) {
      return '--';
    }
    const scopeType = `${report.scopeType || ''}`;
    if (scopeType === 'global') {
      return 'Global';
    }

    const scopeIds = this.normalizeScopeIds(report.scopeIds, report.scopeId);
    return this.formatScopeLabels(scopeType, scopeIds);
  }

  protected getSelectedScopeLabel(scopeType: string): string {
    if (scopeType === 'global') {
      return 'Global';
    }

    return this.formatScopeLabels(scopeType, this.getSelectedScopeIds(scopeType));
  }

  protected getScopeSelectorLabel(scopeType: string): string {
    return scopeType === 'partner' ? 'Partners' : 'Scope selector';
  }

  protected shouldShowSelectSearch(optionCount: number): boolean {
    return optionCount > this.selectSearchThreshold;
  }

  protected getFilteredScopeOptions(scopeType: string): ScopeOption[] {
    const search = `${this.scopeSearchControl.value || ''}`.trim();
    return this.filterItems(this.getScopeOptions(scopeType), search, (scope) => [
      scope.label,
      scope.secondaryLabel,
      scope.type,
    ]);
  }

  protected getDeliveryLabel(deliveryChannel: string | null | undefined): string {
    return this.deliveryChannels.find((item) => item.value === deliveryChannel)?.label || `${deliveryChannel || '--'}`;
  }

  protected getFormatLabel(format: string | null | undefined): string {
    return this.outputFormats.find((item) => item.value === format)?.label || `${format || '--'}`.toUpperCase();
  }

  protected getReportTypeLabel(reportType: string | null | undefined): string {
    return reportType === 'flat_file' ? 'Custom Flat-File Report' : 'Canned Report';
  }

  protected getField(fieldKey: string): ScheduledReportFieldDefinition | null {
    return this.fieldCatalog.find((field) => field.key === fieldKey) || null;
  }

  protected getFieldLabel(fieldKey: string): string {
    return this.getField(fieldKey)?.label || fieldKey;
  }

  protected getTemplateColumnFieldLabel(fieldKey: string): string {
    return this.getFieldLabel(fieldKey);
  }

  protected getFieldOperators(fieldKey: string): string[] {
    return this.getField(fieldKey)?.filterOperators || ['equals'];
  }

  protected getSortableFields(): ScheduledReportFieldDefinition[] {
    if (this.isTemplateReport) {
      return this.getTemplateSortableFields();
    }

    const baseEntity = `${this.reportForm.get('baseEntity')?.value || 'users'}`;
    const sortableFields = this.fieldCatalog.filter((field) => field.sortable && (!field.supportedBaseEntities || field.supportedBaseEntities.includes(baseEntity)));
    if (!this.isAggregatedCustomReport) {
      return sortableFields;
    }

    const allowedFields = this.getAggregationGroupByFieldKeys();
    return sortableFields.filter((field) => allowedFields.has(field.key));
  }

  protected getFilteredTemplates(): ScheduledReportTemplate[] {
    const search = `${this.templateSearchControl.value || ''}`.trim();
    return this.filterItems(this.templates, search, (template) => [
      template.label,
      template.description,
      template.key,
    ]);
  }

  protected getFilteredVendorOptions(searchControl: UntypedFormControl = this.templateVendorSearchControl): VendorOption[] {
    const search = `${searchControl.value || ''}`.trim();
    return this.filterItems(this.vendorOptions, search, (vendor) => [vendor.label, vendor.value]);
  }

  protected getFilterValueOptions(control: AbstractControl): ScheduledReportValueOption[] {
    const fieldKey = `${control.get('field')?.value || ''}`;
    const values = this.getFiniteFilterOptions(fieldKey);
    if (!values.length) {
      return [];
    }

    const currentValues = this.filterOperatorUsesMultipleValues(control.get('operator')?.value)
      ? this.normalizeStringArray(control.get('optionValues')?.value)
      : this.normalizeStringArray([control.get('valueText')?.value]);
    const optionsByValue = new Map(values.map((option) => [option.value, option]));
    currentValues.forEach((value) => {
      if (value && !optionsByValue.has(value)) {
        optionsByValue.set(value, { value, label: `${value} (not in catalog)` });
      }
    });
    return Array.from(optionsByValue.values());
  }

  protected filterUsesFiniteOptions(control: AbstractControl): boolean {
    return this.filterOperatorUsesValue(control.get('operator')?.value)
      && ['equals', 'not_equals', 'in', 'not_in'].includes(`${control.get('operator')?.value || ''}`)
      && this.isFiniteOptionField(`${control.get('field')?.value || ''}`);
  }

  protected filterUsesSingleSelect(control: AbstractControl): boolean {
    return this.filterUsesFiniteOptions(control)
      && !this.filterOperatorUsesMultipleValues(control.get('operator')?.value);
  }

  protected filterUsesMultiSelect(control: AbstractControl): boolean {
    return this.filterUsesFiniteOptions(control)
      && this.filterOperatorUsesMultipleValues(control.get('operator')?.value);
  }

  protected getFilteredAvailableProducts(
    vendorSlug?: string | null,
    searchControl: UntypedFormControl = this.semanticProductSearchControl,
  ): ProductOption[] {
    const search = `${searchControl.value || ''}`.trim();
    return this.filterItems(this.getAvailableProducts(vendorSlug), search, (product) => [
      product.productName,
      product.productSlug,
      product.vendorName,
      product.vendorSlug,
      product.label,
    ]);
  }

  protected getFilteredTemplateProducts(): ProductOption[] {
    const search = `${this.templateProductSearchControl.value || ''}`.trim();
    return this.filterItems(this.getAvailableTemplateProducts(), search, (product) => [
      product.productName,
      product.productSlug,
      product.vendorName,
      product.vendorSlug,
      product.label,
    ]);
  }

  protected getFilteredGroupedFields(): Array<{ group: string; fields: ScheduledReportFieldDefinition[] }> {
    const search = `${this.fieldSearchControl.value || ''}`.trim();
    if (!search) {
      return this.groupedFields;
    }

    return this.groupedFields
      .map((group) => ({
        group: group.group,
        fields: this.filterItems(group.fields, search, (field) => [
          field.label,
          field.description,
          field.key,
          field.group,
          field.type,
        ]),
      }))
      .filter((group) => group.fields.length > 0);
  }

  protected getGroupedFieldCount(): number {
    return this.groupedFields.reduce((count, group) => count + group.fields.length, 0);
  }

  protected getFilteredSortableFields(): ScheduledReportFieldDefinition[] {
    const search = `${this.sortFieldSearchControl.value || ''}`.trim();
    return this.filterItems(this.getSortableFields(), search, (field) => [
      field.label,
      field.description,
      field.key,
      field.group,
      field.type,
    ]);
  }

  protected getFieldType(fieldKey: string): string {
    return this.getField(fieldKey)?.type || 'string';
  }

  protected canReportTotalColumn(control: AbstractControl): boolean {
    const kind = `${control.get('kind')?.value || 'field'}` as ReportColumnKind;
    if (kind !== 'field') {
      return false;
    }

    return this.isReportTotalField(`${control.get('field')?.value || ''}`);
  }

  protected isReportTotalField(fieldKey: string): boolean {
    const type = `${this.getField(fieldKey)?.type || ''}`.toLowerCase();
    return this.reportTotalNumericFieldTypes.has(type);
  }

  protected fieldRequiresArgs(fieldKey: string): boolean {
    return this.getField(fieldKey)?.requiresArgs === true;
  }

  protected getFieldArgsSchema(fieldKey: string): Record<string, ScheduledReportFieldArgSchema> {
    return this.getField(fieldKey)?.argsSchema || {};
  }

  protected getFieldArgEntries(fieldKey: string): Array<{ key: string; schema: ScheduledReportFieldArgSchema }> {
    if (this.fieldArgEntriesCacheSource !== this.fieldCatalog) {
      this.fieldArgEntriesCache.clear();
      this.fieldArgEntriesCacheSource = this.fieldCatalog;
    }

    if (!this.fieldArgEntriesCache.has(fieldKey)) {
      this.fieldArgEntriesCache.set(
        fieldKey,
        Object.entries(this.getFieldArgsSchema(fieldKey))
          .filter(([key]) => this.shouldRenderFieldArg(fieldKey, key))
          .map(([key, schema]) => ({ key, schema })),
      );
    }

    return this.fieldArgEntriesCache.get(fieldKey) || [];
  }

  protected getFieldArgLabel(argKey: string, schema?: ScheduledReportFieldArgSchema | null): string {
    if (schema?.label) {
      return schema.label;
    }

    const normalized = `${argKey || ''}`
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[._-]+/g, ' ')
      .trim();

    if (!normalized) {
      return `${argKey || ''}`;
    }

    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }

  protected getArgsGroup(control: AbstractControl): UntypedFormGroup {
    return control.get('args') as UntypedFormGroup;
  }

  protected getColumnSourcesArray(control: AbstractControl): UntypedFormArray {
    return control.get('sources') as UntypedFormArray;
  }

  protected filterOperatorUsesValue(operator: string | null | undefined): boolean {
    return operator !== 'is_blank' && operator !== 'is_not_blank';
  }

  protected filterOperatorUsesMultipleValues(operator: string | null | undefined): boolean {
    return operator === 'in' || operator === 'not_in';
  }

  protected isRuntimeFilter(control: AbstractControl): boolean {
    return `${control.get('valueMode')?.value || 'saved'}` === 'runtime';
  }

  protected setRuntimeFilterMode(control: AbstractControl, isRuntime: boolean): void {
    control.get('valueMode')?.setValue(isRuntime ? 'runtime' : 'saved');
    if (isRuntime && !`${control.get('parameterKey')?.value || ''}`.trim()) {
      control.get('parameterKey')?.setValue(this.createFilterParameterKey());
    }
  }

  protected isVendorSelectionArg(argKey: string, schema: ScheduledReportFieldArgSchema): boolean {
    return argKey === 'vendorSelections' || schema.ui === 'vendor_product_multi_select';
  }

  protected getVendorSelectionDraft(control: AbstractControl): VendorSelectionDraft {
    const argsGroup = this.getArgsGroup(control);
    return {
      vendorSlugs: this.normalizeStringArray(argsGroup?.get('vendorSlugs')?.value),
      productIds: this.normalizeStringArray(argsGroup?.get('productIds')?.value),
    };
  }

  protected getAvailableVendorSelectionProducts(control: AbstractControl): ProductOption[] {
    return this.getAvailableProductsForVendorSlugs(this.getVendorSelectionDraft(control).vendorSlugs);
  }

  protected getFilteredVendorSelectionProducts(
    control: AbstractControl,
    searchControl: UntypedFormControl = this.semanticProductSearchControl,
  ): ProductOption[] {
    const search = `${searchControl.value || ''}`.trim();
    return this.filterItems(this.getAvailableVendorSelectionProducts(control), search, (product) => [
      product.productName,
      product.productSlug,
      product.vendorName,
      product.vendorSlug,
      product.label,
    ]);
  }

  protected getFieldOperatorLabel(operator: string): string {
    return operator.replace(/_/g, ' ');
  }

  protected onColumnKindChange(control: AbstractControl): void {
    const kind = `${control.get('kind')?.value || 'field'}` as ReportColumnKind;
    const nextGroup = this.buildColumnGroup(undefined, kind);
    nextGroup.patchValue({
      header: `${control.get('header')?.value || ''}`.trim(),
    }, { emitEvent: false });
    const index = this.columnsArray.controls.indexOf(control);
    if (index >= 0) {
      this.columnsArray.setControl(index, nextGroup);
    }
  }

  protected onColumnFieldChange(control: AbstractControl): void {
    const field = `${control.get('field')?.value || ''}`;
    this.replaceFieldArgsGroup(control, field);
    if (!this.canReportTotalColumn(control)) {
      control.get('reportTotal')?.setValue(false, { emitEvent: false });
    }
    this.syncColumnDateFormatControls(control, field);
  }

  protected isDateFormatColumn(control: AbstractControl): boolean {
    const fieldType = this.getFieldType(`${control.get('field')?.value || ''}`);
    return `${control.get('kind')?.value || ''}` === 'field' && (fieldType === 'date' || fieldType === 'datetime');
  }

  protected isCustomDateFormatColumn(control: AbstractControl): boolean {
    return this.isDateFormatColumn(control) && `${control.get('dateFormatMode')?.value || ''}` === 'custom';
  }

  protected isDateFormatGroupBy(control: AbstractControl): boolean {
    const fieldType = this.getFieldType(`${control.get('field')?.value || ''}`);
    return fieldType === 'date' || fieldType === 'datetime';
  }

  protected isCustomDateFormatGroupBy(control: AbstractControl): boolean {
    return this.isDateFormatGroupBy(control) && `${control.get('dateFormatMode')?.value || ''}` === 'custom';
  }

  protected onGroupByFieldChange(control: AbstractControl): void {
    const field = `${control.get('field')?.value || ''}`;
    this.replaceFieldArgsGroup(control, field);
    if (!`${control.get('header')?.value || ''}`.trim()) {
      control.get('header')?.setValue(this.getFieldLabel(field), { emitEvent: false });
    }
    this.syncDateFormatControls(control, field);
  }

  protected onCountDistinctFieldChange(): void {
    this.replaceAggregationCountDistinctArgsGroup(`${this.aggregationGroup.get('countDistinctField')?.value || ''}`);
  }

  protected onFilterFieldChange(control: AbstractControl): void {
    const fieldKey = `${control.get('field')?.value || ''}`;
    const operator = this.getFieldOperators(fieldKey)[0] || 'equals';
    control.get('operator')?.setValue(operator, { emitEvent: false });
    control.get('valueText')?.setValue('', { emitEvent: false });
    control.get('multiValueText')?.setValue('', { emitEvent: false });
    control.get('optionValues')?.setValue([], { emitEvent: false });
    this.replaceFieldArgsGroup(control, fieldKey);
  }

  protected onSortFieldChange(control: AbstractControl): void {
    this.replaceFieldArgsGroup(control, `${control.get('field')?.value || ''}`);
  }

  protected onVendorSelectionChange(control: AbstractControl): void {
    const argsGroup = this.getArgsGroup(control);
    const validProductIds = new Set(this.getAvailableVendorSelectionProducts(control).map((product) => product.id));
    const selectedProductIds = this.normalizeStringArray(argsGroup?.get('productIds')?.value);
    argsGroup?.get('productIds')?.setValue(
      selectedProductIds.filter((productId) => validProductIds.has(productId)),
      { emitEvent: false },
    );
  }

  protected addTemplateSource(control: AbstractControl): void {
    this.getColumnSourcesArray(control).push(this.buildTemplateSourceGroup());
  }

  protected removeTemplateSource(control: AbstractControl, index: number): void {
    this.getColumnSourcesArray(control).removeAt(index);
  }

  protected onTemplateSourceFieldChange(control: AbstractControl): void {
    this.replaceFieldArgsGroup(control, `${control.get('field')?.value || ''}`);
  }

  protected usesSubscriptionStatus(): boolean {
    if (this.isTemplateReport) {
      return this.selectedTemplate?.key === 'partner_subscription_roster';
    }
    const controls = [...this.columnsArray.controls, ...this.filtersArray.controls];
    return controls.some((control) => `${control.get('field')?.value || ''}` === 'semantic.vendor_subscription_state_as_of');
  }

  protected getScheduleSummary(report: ScheduledReport | null): string {
    if (!report) {
      return '--';
    }

    const schedule = report.scheduleConfig || {} as any;
    const time = this.formatScheduleClockTime(schedule.time || '00:00');
    const timezone = this.getScheduleTimezoneLabel(report);
    const windowModeLabel = this.windowModes.find((item) => item.value === schedule.windowMode)?.label || schedule.windowMode || 'Window';
    const scheduleContext = this.isTeamMembersRuntimeSnapshotReport(report)
      ? timezone
      : `${timezone}, ${windowModeLabel}`;

    if (report.scheduleType === 'monthly') {
      return `Monthly on day ${schedule.dayOfMonth || 1} at ${time} (${scheduleContext})`;
    }
    if (report.scheduleType === 'weekly') {
      const days = (schedule.daysOfWeek || [])
        .map((day: number) => this.dayOptions.find((item) => item.value === day)?.label)
        .filter(Boolean)
        .join(', ');
      return `Weekly on ${days || 'Mon'} at ${time} (${scheduleContext})`;
    }
    return `Daily at ${time} (${scheduleContext})`;
  }

  private isTeamMembersRuntimeSnapshotReport(report: ScheduledReport): boolean {
    const baseEntity = `${report.baseEntity || report.definitionJson?.baseEntity || ''}`;
    const asOfMode = `${report.definitionJson?.temporal?.asOfMode || 'run_time'}`;
    return report.reportType === 'flat_file' && baseEntity === 'teamusers' && asOfMode === 'run_time';
  }

  protected formatReportDateTime(value: string | null | undefined, timezone?: string | null): string {
    if (!value) {
      return '--';
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return `${value}`;
    }

    return new Intl.DateTimeFormat('en-US', {
      timeZone: this.getDisplayTimezone(timezone),
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    }).format(parsedDate);
  }

  protected formatReportDateRange(
    startValue: string | null | undefined,
    endValue: string | null | undefined,
    timezone?: string | null,
  ): string {
    const start = this.formatReportDateTime(startValue, timezone);
    const end = this.formatReportDateTime(endValue, timezone);
    if (start === '--' && end === '--') {
      return '--';
    }
    return `${start} - ${end}`;
  }

  protected shouldIncludeEditorReportDates(): boolean {
    return this.reportForm.get('showReportDates')?.value === true;
  }

  protected getLastResultLabel(report: ScheduledReport): string {
    if (report.lastErrorMessage) {
      return 'Failed';
    }
    if (report.lastSuccessRunId) {
      return 'Succeeded';
    }
    if (report.status === 'draft') {
      return 'Draft';
    }
    return 'Pending';
  }

  protected getColumnPreviewHeaders(preview: ScheduledReportPreview | null): string[] {
    return (preview?.columns || []).map((column, index) => column.header || column.field || `Column ${index + 1}`);
  }

  protected getDisplayColumnHeader(header: string): string {
    return this.isSurveyAnswersColumn(header) ? 'Answers' : header;
  }

  protected getDetailOutputColumns(report: ScheduledReport | null): string[] {
    if (!report) {
      return [];
    }

    if (this.detailPreview?.columns?.length) {
      return this.detailPreview.columns.map((column, index) => column.header || column.field || `Column ${index + 1}`);
    }

    if (`${report.reportType}` === 'template') {
      return this.getOrderedTemplateColumns(
        this.getTemplateByKey(report.templateKey),
        (report.templateArgs?.columnOverrides || []) as ScheduledReportTemplateColumnOverride[],
      ).map((column) => column.header);
    }

    const aggregation = report.definitionJson?.aggregation;
    if (aggregation?.groupBy?.length) {
      const countHeader = this.getAggregationCountHeader(aggregation);
      return [
        ...aggregation.groupBy.map((level) => `${level.header || this.getFieldLabel(level.field)}`.trim()),
        aggregation.reportTotal ? `${countHeader} (report total)` : countHeader,
      ];
    }

    return (report.definitionJson?.columns || []).map((column, index) => {
      const header = column.header || this.getFieldLabel(column.field || `Column ${index + 1}`);
      return column.reportTotal ? `${header} (report total)` : header;
    });
  }

  protected getDetailSortRules(report: ScheduledReport | null): Array<{ field: string; direction: 'asc' | 'desc' }> {
    if (!report) {
      return [];
    }

    const configuredSort = report.definitionJson?.sort || [];
    if (configuredSort.length) {
      return configuredSort.map((sortItem) => ({
        field: sortItem.field,
        direction: this.normalizeSortDirection(sortItem.direction),
      }));
    }

    if (`${report.reportType}` !== 'template') {
      return [];
    }

    return this.getSortedTemplateDefaultSort(this.getTemplateByKey(report.templateKey)).map((sortItem) => ({
      field: sortItem.field,
      direction: sortItem.direction,
    }));
  }

  protected getSortDirectionLabel(direction: string | null | undefined): string {
    return `${direction || 'asc'}`.toLowerCase() === 'desc' ? 'Descending' : 'Ascending';
  }

  protected getPreviewCellValue(
    preview: ScheduledReportPreview | null,
    row: Record<string, any> | null | undefined,
    header: string,
  ): any {
    if (!row) {
      return undefined;
    }

    if (row[header] !== undefined) {
      return row[header];
    }

    const column = (preview?.columns || []).find((item) => (item.header || item.field) === header);
    if (column?.field && row[column.field] !== undefined) {
      return row[column.field];
    }

    return undefined;
  }

  protected getPreviewCellText(header: string, value: any): string {
    if (this.isSurveyAnswersColumn(header)) {
      return this.formatSurveyAnswersValue(value);
    }

    if (value === null || value === undefined) {
      return '';
    }

    const formattedDate = this.formatPreviewDateValue(value);
    if (formattedDate !== null) {
      return formattedDate;
    }

    if (typeof value === 'object') {
      return JSON.stringify(value);
    }

    return `${value}`;
  }

  protected isPreviewCellMultiline(header: string, value: any): boolean {
    return this.isSurveyAnswersColumn(header) || `${this.getPreviewCellText(header, value)}`.includes('\n');
  }

  protected shouldUseScrollablePreview(preview: ScheduledReportPreview | null): boolean {
    return (preview?.sampleRows?.length || 0) > 20;
  }

  protected shouldShowPreviewTotals(preview: ScheduledReportPreview | null, target: PreviewSortTarget): boolean {
    return this.getPreviewTotalHeaders(preview, target).length > 0;
  }

  protected isPreviewTotalLabelColumn(preview: ScheduledReportPreview | null, target: PreviewSortTarget, header: string): boolean {
    return this.getPreviewTotalLabelHeader(preview, target) === header;
  }

  protected isPreviewTotalValueColumn(preview: ScheduledReportPreview | null, target: PreviewSortTarget, header: string): boolean {
    return this.getPreviewTotalHeaders(preview, target).includes(header);
  }

  protected getPreviewTotalCellText(preview: ScheduledReportPreview | null, target: PreviewSortTarget, header: string): string {
    const isLabelColumn = this.isPreviewTotalLabelColumn(preview, target, header);
    const isValueColumn = this.isPreviewTotalValueColumn(preview, target, header);
    if (!isLabelColumn && !isValueColumn) {
      return '';
    }

    const total = this.getPreviewTotalValue(preview, header);
    const totalText = Number.isFinite(total) ? `${this.roundReportTotal(total)}` : '';
    if (isLabelColumn && isValueColumn) {
      return totalText ? `TOTAL ${totalText}` : 'TOTAL';
    }
    if (isLabelColumn) {
      return 'TOTAL';
    }

    return totalText;
  }

  protected isPreviewTotalFallbackSampleOnly(preview: ScheduledReportPreview | null, target: PreviewSortTarget): boolean {
    return this.shouldShowPreviewTotals(preview, target)
      && !this.hasBackendPreviewTotals(preview)
      && (preview?.totalCount || 0) > (preview?.sampleRows?.length || 0);
  }

  protected togglePreviewSort(target: PreviewSortTarget, header: string): void {
    const current = this.previewSortState[target];
    if (!current || current.header !== header) {
      this.previewSortState[target] = { header, direction: 'asc' };
      return;
    }

    this.previewSortState[target] = {
      header,
      direction: current.direction === 'asc' ? 'desc' : 'asc',
    };
  }

  protected getPreviewSortDirection(target: PreviewSortTarget, header: string): PreviewSortDirection | null {
    const current = this.previewSortState[target];
    return current?.header === header ? current.direction : null;
  }

  protected getSortedPreviewRows(preview: ScheduledReportPreview | null, target: PreviewSortTarget): Array<Record<string, any>> {
    const rows = preview?.sampleRows || [];
    const current = this.previewSortState[target];
    if (!current?.header) {
      return rows;
    }

    const sorted = [...rows];
    sorted.sort((left, right) => {
      const leftValue = this.getPreviewSortValue(current.header, this.getPreviewCellValue(preview, left, current.header));
      const rightValue = this.getPreviewSortValue(current.header, this.getPreviewCellValue(preview, right, current.header));
      if (leftValue < rightValue) {
        return current.direction === 'asc' ? -1 : 1;
      }
      if (leftValue > rightValue) {
        return current.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
    return sorted;
  }

  protected objectKeys(value: Record<string, any> | null | undefined): string[] {
    return Object.keys(value || {});
  }

  protected asFormGroup(control: AbstractControl): UntypedFormGroup {
    return control as UntypedFormGroup;
  }

  protected trackById(index: number, item: { id?: string; key?: string; value?: string }): string {
    return item.id || item.key || item.value || `${index}`;
  }

  protected trackGroupProductOnboardingRow(index: number, row: CompanyGroupProductOnboardingRow): string {
    return [
      row.companyId || '',
      row.requestedVendorProductId || '',
      row.vendorGroupProductId || '',
      row.status || '',
      `${index}`,
    ].join(':');
  }

  protected trackByGroupName(index: number, item: { group?: string }): string {
    return item.group || `${index}`;
  }

  protected trackByArgKey(index: number, item: { key?: string }): string {
    return item.key || `${index}`;
  }

  protected getAvailableProducts(vendorSlug?: string | null): ProductOption[] {
    const normalizedVendorSlug = `${vendorSlug || ''}`.trim();
    return this.productOptions.filter((product) => !normalizedVendorSlug || product.vendorSlug === normalizedVendorSlug);
  }

  protected getFiniteFilterOptions(fieldKey: string): ScheduledReportValueOption[] {
    return this.reportValueOptions[fieldKey] || [];
  }

  private isFiniteOptionField(fieldKey: string): boolean {
    return Object.prototype.hasOwnProperty.call(this.reportValueOptions, fieldKey);
  }

  private getReportValueOptionMap(): Record<string, ScheduledReportValueOption[]> {
    return this.reportValueOptions;
  }

  protected getAvailableTemplateProducts(): ProductOption[] {
    return this.getAvailableProductsForVendorSlugs(this.getSelectedTemplateVendorSlugs());
  }

  protected getTemplateProductOptionValue(product: ProductOption): string {
    return product.id;
  }

  protected onTemplateVendorSelectionChange(): void {
    const vendorSlugsControl = this.templateArgsGroup.get('vendorSlugs');
    if (!vendorSlugsControl) {
      return;
    }

    const rawVendorSlugs = this.normalizeStringArray(vendorSlugsControl.value);
    const nextVendorSlugs = rawVendorSlugs.includes(this.allTemplateVendorsValue)
      ? this.getDefaultTemplateVendorSlugs()
      : rawVendorSlugs;

    this.applyTemplateVendorSelection(nextVendorSlugs);
  }

  protected onAllTemplateVendorsToggle(event: any): void {
    if (!event?.isUserInput) {
      return;
    }

    const allVendorSlugs = this.getDefaultTemplateVendorSlugs();
    const currentVendorSlugs = this.getSelectedTemplateVendorSlugs();
    const nextVendorSlugs = currentVendorSlugs.length === allVendorSlugs.length ? [] : allVendorSlugs;

    this.applyTemplateVendorSelection(nextVendorSlugs);
    event?.source?.deselect?.();
  }

  protected resetTemplateProductSelection(): void {
    this.templateArgsGroup.get('productSlugs')?.setValue([]);
  }

  protected resetSemanticProductSelection(control: AbstractControl): void {
    control.get('productSlugs')?.setValue([]);
  }

  private configureFormSubscriptions(): void {
    this.reportForm.valueChanges.subscribe(() => {
      if (this.editorSensitivePreviewMode === 'revealed') {
        this.preview = null;
        this.previewSignature = '';
        this.clearEditorSensitivePreviewState();
      }
    });

    this.reportForm.get('reportType')?.valueChanges.subscribe((value) => {
      this.activeListTab = (value || 'template') as ReportTabType;
      this.updateDynamicValidators();
      if (value === 'template') {
        this.applyTemplateDefaults(this.selectedTemplate?.key || this.templates[0]?.key || '');
      }
    });

    this.reportForm.get('templateKey')?.valueChanges.subscribe((value) => {
      if (this.isTemplateReport) {
        this.applyTemplateDefaults(`${value || ''}`);
      }
    });

    this.reportForm.get('resultMode')?.valueChanges.subscribe((value) => {
      if (value === 'aggregated') {
        this.syncAggregationCountDistinctField();
      }
    });

    this.reportForm.get('baseEntity')?.valueChanges.subscribe(() => {
      this.reconcileFieldsForCurrentBaseEntity();
      this.syncAggregationCountDistinctField();
    });

    this.reportForm.get('deliveryChannel')?.valueChanges.subscribe(() => {
      this.updateDynamicValidators();
    });

    this.reportForm.get('scopeType')?.valueChanges.subscribe((value) => {
      void this.handleScopeTypeChange(value as ScopeType);
    });

    this.reportForm.get('scheduleType')?.valueChanges.subscribe(() => {
      this.updateDynamicValidators();
    });

    this.reportForm.get('scheduleConfig.windowMode')?.valueChanges.subscribe(() => {
      this.updateDynamicValidators();
    });
  }

  private getGroupProductOnboardingReadyKey(row: CompanyGroupProductOnboardingRow): string {
    return [
      row?.companyId || '',
      row?.requestedVendorProductId || '',
    ].join(':');
  }

  private getDeleteGroupProductOnboardingRequestConfirmationCopy(row: CompanyGroupProductOnboardingRow): string {
    const productLabel = `${row?.providerName || 'This provider'} - ${row?.planName || 'this plan'}`.trim();
    const companyLabel = `${row?.companyName || 'this company'}`.trim();
    if (row?.status === 'ready') {
      return `Delete the approval record for <strong>${productLabel}</strong> on <strong>${companyLabel}</strong>? This will make the plan require approval again for Payroll Admin companies.`;
    }
    return `Delete the approval request for <strong>${productLabel}</strong> on <strong>${companyLabel}</strong>?`;
  }

  private async loadInitialState(): Promise<void> {
    this.isBootstrapping = true;
    this.isGroupProductOnboardingReportLoading = this.canViewGroupProductOnboardingReport;
    try {
      const [companies, catalog, templateResponse, sftpSecretCatalog, partners, groupProductOnboardingReportRows] = await Promise.all([
        this.loadCompanyOptions(),
        this.scheduledReportsService.getFieldCatalog(),
        this.scheduledReportsService.getTemplates(),
        this.scheduledReportsService.getSftpSecretCatalog(),
        this.loadPartnerOptions(),
        this.canViewGroupProductOnboardingReport
          ? this.teamCompaniesService.getGroupProductOnboardingReport().catch(() => [])
          : Promise.resolve([]),
      ]);

      this.companies = companies;
      this.teams = [];
      this.hasLoadedTeamOptions = false;
      this.teamOptionsLoadPromise = null;
      this.partners = partners;
      this.fieldCatalog = catalog.fields || [];
      this.sensitivePreviewBusinessPurposes = catalog.sensitivePreviewBusinessPurposes || [];
      this.baseEntities = catalog.baseEntities || [];
      this.templates = templateResponse.templates || [];
      this.applySftpSecretCatalog(sftpSecretCatalog);
      this.reportValueOptions = buildScheduledReportValueOptions(catalog);
      this.groupProductOnboardingReportRows = (groupProductOnboardingReportRows || [])
        .slice()
        .sort((left, right) => {
          const leftLabel = [
            left?.companyName || '',
            left?.providerName || '',
            left?.planName || '',
          ].join(' ');
          const rightLabel = [
            right?.companyName || '',
            right?.providerName || '',
            right?.planName || '',
          ].join(' ');
          return leftLabel.localeCompare(rightLabel);
        });
      this.productOptions = await this.loadProductOptions();
      this.ensureScopeSelection();
      await this.reloadReports();
      await this.applyRouteContext();
    } catch (error: any) {
      this.presentError(error, 'Unable to load the scheduled reports workspace.');
    } finally {
      this.isCompanyStateLoading = false;
      this.isBootstrapping = false;
      this.isGroupProductOnboardingReportLoading = false;
      this.updateDynamicValidators();
    }
  }

  protected async addSftpSecret(
    type: ScheduledReportSftpSecretType,
    controlName: 'passwordSecretKey' | 'privateKeySecretKey' | 'publicKeySecretKey',
  ): Promise<void> {
    const dialogRef = this.dialog.open<
      PayrollReportSftpSecretDialogComponent,
      { type: ScheduledReportSftpSecretType; prefix: string },
      PayrollReportSftpSecretDialogResult
    >(PayrollReportSftpSecretDialogComponent, {
      width: '640px',
      data: { type, prefix: this.sftpSecretPrefixes[type] },
      autoFocus: false,
      disableClose: true,
    });
    const result = await dialogRef.afterClosed().toPromise();
    if (!result) {
      return;
    }

    try {
      const created = await this.scheduledReportsService.createSftpSecret(type, result.name, result.value);
      const references = this.getSftpSecretReferences(type);
      if (!references.some((secret) => secret.reference === created.reference)) {
        references.push({ name: created.name, reference: created.reference });
        references.sort((left, right) => left.name.localeCompare(right.name));
      }
      this.deliveryConfigGroup.get(controlName)?.setValue(created.reference);
      this.snackBar.open(`${this.getSftpSecretTypeLabel(type)} secret created.`, 'Dismiss', { duration: 3000 });
    } catch (error: any) {
      this.presentError(error, `Unable to create the ${this.getSftpSecretTypeLabel(type)} secret.`);
    }
  }

  private applySftpSecretCatalog(catalog: ScheduledReportSftpSecretCatalog): void {
    this.sftpPasswordSecretReferences = [...catalog.passwords];
    this.sftpPrivateKeySecretReferences = [...catalog.privateKeys];
    this.sftpPgpPublicKeySecretReferences = [...catalog.pgpPublicKeys];
  }

  private getSftpSecretReferences(type: ScheduledReportSftpSecretType): ScheduledReportSftpSecretReference[] {
    switch (type) {
      case 'password':
        return this.sftpPasswordSecretReferences;
      case 'private_key':
        return this.sftpPrivateKeySecretReferences;
      case 'pgp_public_key':
        return this.sftpPgpPublicKeySecretReferences;
    }
  }

  private getSftpSecretTypeLabel(type: ScheduledReportSftpSecretType): string {
    switch (type) {
      case 'password':
        return 'SFTP password';
      case 'private_key':
        return 'SFTP private key';
      case 'pgp_public_key':
        return 'PGP public key';
    }
  }

  private async loadPartnerOptions(): Promise<ScopeOption[]> {
    const collected = new Map<string, ScopeOption>();

    try {
      const visiblePartners = await this.usersService.getVisibleBrands();
      this.normalizePartners(visiblePartners).forEach((partner) => {
        collected.set(partner.id, partner);
      });
    } catch {
      // Fall through to the public catalog when visible brands are unavailable.
    }

    if (!collected.size) {
      try {
        const allPartners = await this.usersService.getBrands();
        this.normalizePartners(allPartners).forEach((partner) => {
          collected.set(partner.id, partner);
        });
      } catch {
        return [];
      }
    }

    return Array.from(collected.values()).sort((left, right) => left.label.localeCompare(right.label));
  }

  private async applyRouteContext(): Promise<void> {
    const queryParams = this.route.snapshot.queryParamMap;
    const queryKey = queryParams.keys
      .slice()
      .sort()
      .map((key) => `${key}=${queryParams.getAll(key).join(',')}`)
      .join('&');

    if (!queryKey || queryKey === this.lastHandledRouteQueryKey) {
      return;
    }

    const reportId = `${queryParams.get('reportId') || ''}`.trim();
    if (reportId) {
      const report = this.reports.find((item) => item.id === reportId);
      if (report) {
        this.lastHandledRouteQueryKey = queryKey;
        await this.openReport(report);
      }
      return;
    }

    const create = `${queryParams.get('create') || ''}`.trim();
    if (create) {
      const tab = create === 'flat_file' ? 'flat_file' : 'template';
      this.startCreate(tab);
      this.applyCreateRouteOverrides(queryParams, tab);
      this.lastHandledRouteQueryKey = queryKey;
    }
  }

  private applyCreateRouteOverrides(queryParams: ParamMap, tab: ReportTabType): void {
    const templateKey = `${queryParams.get('templateKey') || ''}`.trim();
    const reportName = `${queryParams.get('name') || ''}`.trim();
    const status = `${queryParams.get('status') || ''}`.trim();
    const scopeType = `${queryParams.get('scopeType') || ''}`.trim();
    const scopeIds = this.parseScopeIdsFromQuery(queryParams);

    if (reportName) {
      this.reportForm.get('name')?.setValue(reportName);
    }
    if (status === 'draft' || status === 'active' || status === 'paused') {
      this.reportForm.get('status')?.setValue(status);
    }
    if ((scopeType === 'partner' || scopeType === 'company' || scopeType === 'team' || scopeType === 'global')) {
      this.reportForm.get('scopeType')?.setValue(scopeType);
      this.reportForm.get('scopeIds')?.setValue(scopeType === 'global' ? [] : scopeIds);
    }
    if (tab === 'template' && templateKey) {
      this.reportForm.get('templateKey')?.setValue(templateKey);
    }
  }

  private parseScopeIdsFromQuery(queryParams: ParamMap): string[] {
    const explicitScopeIds = queryParams.getAll('scopeId');
    const csvScopeIds = `${queryParams.get('scopeIds') || ''}`
      .split(',')
      .map((value) => `${value || ''}`.trim())
      .filter(Boolean);

    return Array.from(new Set([...explicitScopeIds, ...csvScopeIds].map((value) => `${value || ''}`.trim()).filter(Boolean)));
  }

  private async loadCompanyOptions(): Promise<ScopeOption[]> {
    const collected = new Map<string, ScopeOption>();
    this.useAllTeamsLookup = false;

    try {
      const teamCompanies = await this.usersService.getTeamCompanies();
      this.normalizeCompanies(teamCompanies).forEach((company) => {
        collected.set(company.id, company);
      });
    } catch {
      // Fall through to the broader companies list below.
    }

    if (!collected.size) {
      try {
        this.useAllTeamsLookup = true;
        const visibleCompanies = await this.teamCompaniesService.getCompanies();
        this.normalizeCompanies(visibleCompanies).forEach((company) => {
          collected.set(company.id, company);
        });
      } catch {
        return [];
      }
    }

    return Array.from(collected.values()).sort((left, right) => left.label.localeCompare(right.label));
  }

  private async loadTeamsForCompanies(companies: ScopeOption[]): Promise<ScopeOption[]> {
    if (this.useAllTeamsLookup) {
      try {
        const allTeams = await this.teamsService.getAllTeams();
        return this.sortAndDeduplicateScopeOptions(this.normalizeTeams(allTeams));
      } catch {
        return [];
      }
    }

    const requests = companies.map(async (company) => {
      try {
        const teams = await this.teamsService.getTeamsForCompany(company.id);
        return this.normalizeTeams(teams, company.label);
      } catch {
        return [];
      }
    });

    const results = (await Promise.all(requests)).reduce((all: ScopeOption[], current: ScopeOption[]) => all.concat(current), []);
    if (results.length) {
      return this.sortAndDeduplicateScopeOptions(results);
    }

    try {
      const allTeams = await this.teamsService.getAllTeams();
      return this.sortAndDeduplicateScopeOptions(this.normalizeTeams(allTeams));
    } catch {
      return [];
    }
  }

  private async loadProductOptions(): Promise<ProductOption[]> {
    try {
      const products = await this.vendorProductsService.getAllVendorProducts();
      return (products || [])
        .filter((product: any) => !!product?.id && !!product?.productSlug && !!product?.vendorSlug)
        .map((product: any) => ({
          id: product.id,
          label: `${product.vendorName || product.vendorSlug || 'Vendor'} / ${product.productName || product.productSlug || product.id}`,
          vendorSlug: product.vendorSlug || '',
          productSlug: product.productSlug || '',
          vendorName: product.vendorName || '',
          productName: product.productName || '',
        }))
        .sort((left: ProductOption, right: ProductOption) => left.label.localeCompare(right.label));
    } catch {
      return [];
    }
  }

  private normalizeCompanies(companies: any[]): ScopeOption[] {
    return this.sortAndDeduplicateScopeOptions((companies || []).map((company: any) => ({
      id: company.id,
      label: company.name || 'Untitled company',
      type: 'company' as const,
    })));
  }

  private normalizePartners(partners: any[]): ScopeOption[] {
    return this.sortAndDeduplicateScopeOptions((partners || [])
      .map((partner: any) => ({
        id: partner.partnerId || partner.id || partner.shortName,
        label: partner.name || partner.shortName || partner.partnerId || 'Untitled partner',
        type: 'partner' as const,
        secondaryLabel: partner.shortName || '',
      }))
      .filter((partner: ScopeOption) => !!partner.id));
  }

  private normalizeTeams(teams: any[], companyLabel = ''): ScopeOption[] {
    return (teams || [])
      .map((team: any) => ({
        id: team.id,
        label: team.name || 'Untitled team',
        type: 'team' as const,
        secondaryLabel: companyLabel || team.companyName || team.company?.name || '',
      }))
      .filter((team: ScopeOption) => !!team.id);
  }

  private sortAndDeduplicateScopeOptions(options: ScopeOption[]): ScopeOption[] {
    const collected = new Map<string, ScopeOption>();
    options.forEach((option) => {
      if (!option?.id || collected.has(option.id)) {
        return;
      }
      collected.set(option.id, option);
    });
    return Array.from(collected.values()).sort((left, right) => left.label.localeCompare(right.label));
  }

  private async handleScopeTypeChange(scopeType: ScopeType): Promise<void> {
    if (scopeType === 'team') {
      await this.ensureTeamOptionsLoaded();
    }
    this.ensureScopeSelection(scopeType);
    this.applyAudienceDefaults();
    this.updateDynamicValidators();
  }

  private async ensureTeamOptionsLoaded(): Promise<void> {
    if (this.hasLoadedTeamOptions) {
      return;
    }
    if (this.teamOptionsLoadPromise) {
      return this.teamOptionsLoadPromise;
    }

    this.areTeamOptionsLoading = true;
    this.teamOptionsLoadPromise = (async () => {
      try {
        this.teams = await this.loadTeamsForCompanies(this.companies);
        this.hasLoadedTeamOptions = true;
      } finally {
        this.areTeamOptionsLoading = false;
        this.teamOptionsLoadPromise = null;
      }
    })();

    return this.teamOptionsLoadPromise;
  }

  private ensureScopeSelection(scopeType?: ScopeType, preserveUnknownSelection = false): void {
    const selectedScopeType = scopeType || this.reportForm.get('scopeType')?.value as ScopeType;
    const options = this.getScopeOptions(selectedScopeType);
    const scopeIdsControl = this.reportForm.get('scopeIds');
    const currentScopeIds = this.getSelectedScopeIds(selectedScopeType);
    if (selectedScopeType === 'global') {
      scopeIdsControl?.setValue([], { emitEvent: false });
      return;
    }
    if (!options.length) {
      if (!preserveUnknownSelection || !currentScopeIds.length) {
        scopeIdsControl?.setValue([], { emitEvent: false });
      }
      return;
    }
    const validScopeIds = currentScopeIds.filter((scopeId) => options.some((item) => item.id === scopeId));
    if (validScopeIds.length) {
      scopeIdsControl?.setValue(validScopeIds, { emitEvent: false });
      return;
    }
    if (preserveUnknownSelection && currentScopeIds.length) {
      scopeIdsControl?.setValue(currentScopeIds, { emitEvent: false });
      return;
    }
    scopeIdsControl?.setValue([options[0]?.id || ''], { emitEvent: false });
  }

  private applyAudienceDefaults(): void {
    const customControl = this.reportForm.get('customAudience.includeUsersWithoutCurrentTeamMembership');
    const templateControl = this.reportForm.get('templateArgs.includeUsersWithoutCurrentTeamMembership');
    if (customControl?.pristine) {
      customControl.setValue(true, { emitEvent: false });
    }
    if (templateControl?.pristine) {
      templateControl.setValue(true, { emitEvent: false });
    }
  }

  private updateDynamicValidators(): void {
    const scopeIdsControl = this.reportForm.get('scopeIds');
    if (this.reportForm.get('scopeType')?.value === 'global') {
      scopeIdsControl?.clearValidators();
    } else {
      scopeIdsControl?.setValidators([Validators.required]);
    }
    scopeIdsControl?.updateValueAndValidity({ emitEvent: false });

    const templateKeyControl = this.reportForm.get('templateKey');
    const baseEntityControl = this.reportForm.get('baseEntity');
    if (this.isTemplateReport) {
      templateKeyControl?.setValidators([Validators.required]);
      baseEntityControl?.clearValidators();
    } else {
      templateKeyControl?.clearValidators();
      baseEntityControl?.setValidators([Validators.required]);
    }
    templateKeyControl?.updateValueAndValidity({ emitEvent: false });
    baseEntityControl?.updateValueAndValidity({ emitEvent: false });

    const deliveryGroup = this.reportForm.get('deliveryConfig') as UntypedFormGroup;
    ['host', 'username', 'toText'].forEach((fieldName) => {
      deliveryGroup.get(fieldName)?.clearValidators();
      deliveryGroup.get(fieldName)?.updateValueAndValidity({ emitEvent: false });
    });
    if (this.reportForm.get('deliveryChannel')?.value === 'ftp') {
      deliveryGroup.get('host')?.setValidators([Validators.required]);
      deliveryGroup.get('username')?.setValidators([Validators.required]);
    } else {
      deliveryGroup.get('toText')?.setValidators([Validators.required]);
    }
    ['host', 'username', 'toText'].forEach((fieldName) => {
      deliveryGroup.get(fieldName)?.updateValueAndValidity({ emitEvent: false });
    });

    const scheduleGroup = this.reportForm.get('scheduleConfig') as UntypedFormGroup;
    scheduleGroup.get('dayOfMonth')?.clearValidators();
    scheduleGroup.get('rollingDays')?.clearValidators();
    if (this.isMonthlySchedule) {
      scheduleGroup.get('dayOfMonth')?.setValidators([Validators.required, Validators.min(1), Validators.max(31)]);
    }
    if (this.isRollingWindow) {
      scheduleGroup.get('rollingDays')?.setValidators([Validators.required, Validators.min(1)]);
    }
    scheduleGroup.get('dayOfMonth')?.updateValueAndValidity({ emitEvent: false });
    scheduleGroup.get('rollingDays')?.updateValueAndValidity({ emitEvent: false });
  }

  private applyTemplateDefaults(templateKey: string): void {
    const template = this.templates.find((item) => item.key === templateKey) || this.templates[0] || null;
    if (!template) {
      return;
    }

    if (!templateKey) {
      this.reportForm.get('templateKey')?.setValue(template.key, { emitEvent: false });
    }
    this.reportForm.get('outputFormat')?.setValue(template.defaultOutputFormat, { emitEvent: false });
    if (!`${this.reportForm.get('fileNamePattern')?.value || ''}`.trim()) {
      this.reportForm.get('fileNamePattern')?.setValue(template.defaultFileNamePattern, { emitEvent: false });
    }
    this.reportForm.get('baseEntity')?.setValue(template.baseEntity, { emitEvent: false });

    const vendorSlugsControl = this.reportForm.get('templateArgs.vendorSlugs');
    const vendorSlugControl = this.reportForm.get('templateArgs.vendorSlug');
    const productSlugsControl = this.reportForm.get('templateArgs.productSlugs');
    if (this.selectedTemplateNeedsVendorSelection) {
      const currentVendorSlugs = this.getSelectedTemplateVendorSlugs(vendorSlugsControl?.value);
      const nextVendorSlugs = currentVendorSlugs.length ? currentVendorSlugs : this.getDefaultTemplateVendorSlugs();
      vendorSlugsControl?.setValue(nextVendorSlugs, { emitEvent: false });
      vendorSlugControl?.setValue(nextVendorSlugs[0] || '', { emitEvent: false });
    } else {
      vendorSlugsControl?.setValue([], { emitEvent: false });
      vendorSlugControl?.setValue('', { emitEvent: false });
    }
    if (productSlugsControl && !Array.isArray(productSlugsControl.value)) {
      productSlugsControl.setValue([], { emitEvent: false });
    }

    this.syncTemplateColumns(template);
    this.syncTemplateSort(template);
  }

  private buildColumnGroup(
    field: ScheduledReportFieldDefinition | null = null,
    kind: ReportColumnKind = 'field',
    column?: Partial<ScheduledReportDefinitionColumn> | null,
  ): UntypedFormGroup {
    const resolvedField = field || (column?.field ? this.getField(column.field) : null) || this.getDefaultFieldForColumns();
    const resolvedKind = column?.template !== undefined
      ? 'template'
      : Object.prototype.hasOwnProperty.call(column || {}, 'value')
        ? 'value'
        : kind;
    const header = `${column?.header || resolvedField?.label || ''}`.trim();

    return this.formBuilder.group({
      kind: [resolvedKind, Validators.required],
      field: [resolvedKind === 'field' ? (column?.field || resolvedField?.key || '') : (column?.field || '')],
      header: [header, Validators.required],
      transform: [resolvedKind === 'field' ? (column?.transform || '') : ''],
      dateFormatMode: [this.getInitialColumnDateFormatMode(column, resolvedField)],
      customDateFormat: [this.getInitialCustomColumnDateFormat(column)],
      reportTotal: [resolvedKind === 'field' ? column?.reportTotal === true : false],
      template: [resolvedKind === 'template' ? `${column?.template || ''}` : ''],
      value: [resolvedKind === 'value' ? (column?.value ?? '') : ''],
      args: this.buildFieldArgsGroup(resolvedKind === 'field' ? (column?.field || resolvedField?.key || '') : '', column?.args || {}),
      sources: this.formBuilder.array(
        resolvedKind === 'template'
          ? this.buildTemplateSourceGroups(column?.sources || {})
          : [],
      ),
    });
  }

  private buildTemplateColumnGroup(column: ScheduledReportTemplateColumn): UntypedFormGroup {
    return this.formBuilder.group({
      field: [column.field, Validators.required],
      defaultHeader: [column.header],
      header: [column.header, Validators.required],
      reportTotal: [(column as ScheduledReportTemplateColumn & { reportTotal?: boolean }).reportTotal === true],
    });
  }

  private getInitialColumnDateFormatMode(
    column: Partial<ScheduledReportDefinitionColumn> | null | undefined,
    field: ScheduledReportFieldDefinition | null,
  ): DateFormatMode {
    const rawMode = `${column?.dateFormatMode || ''}`;
    if (this.isDateFormatMode(rawMode)) {
      return rawMode;
    }

    const rawFormat = `${column?.dateFormat || ''}`.trim();
    if (rawFormat === 'MMM D, YYYY') {
      return 'date';
    }
    if (rawFormat === 'MMM D, YYYY, h:mm:ss A') {
      return 'datetime';
    }
    if (rawFormat === 'MMM D, YYYY, h:mm:ss A z') {
      return 'datetime_timezone';
    }
    if (rawFormat) {
      return 'custom';
    }

    return field?.type === 'date' ? 'date' : 'datetime_timezone';
  }

  private getInitialCustomColumnDateFormat(column: Partial<ScheduledReportDefinitionColumn> | null | undefined): string {
    const rawFormat = `${column?.dateFormat || ''}`.trim();
    return this.isPresetDateFormat(rawFormat) ? '' : rawFormat;
  }

  private isDateFormatMode(value: string): value is DateFormatMode {
    return this.dateFormatOptions.some((option) => option.value === value);
  }

  private isPresetDateFormat(value: string): boolean {
    return [
      'MMM D, YYYY',
      'MMM D, YYYY, h:mm:ss A',
      'MMM D, YYYY, h:mm:ss A z',
    ].includes(value);
  }

  private buildSortGroup(
    sortItem?: Partial<ScheduledReportTemplateSort & { args?: Record<string, any> }>,
  ): UntypedFormGroup {
    return this.formBuilder.group({
      field: [`${sortItem?.field || ''}`.trim(), Validators.required],
      direction: [this.normalizeSortDirection(sortItem?.direction), Validators.required],
      args: this.buildFieldArgsGroup(`${sortItem?.field || ''}`.trim(), sortItem?.args || {}),
    });
  }

  private buildAggregationGroupByControl(
    field: ScheduledReportFieldDefinition | null = null,
    groupBy?: Partial<ScheduledReportDefinitionAggregationLevel> | null,
  ): UntypedFormGroup {
    const resolvedField = field || (groupBy?.field ? this.getField(groupBy.field) : null) || this.getDefaultFieldForColumns();
    const fieldKey = `${groupBy?.field || resolvedField?.key || ''}`.trim();
    const header = `${groupBy?.header || resolvedField?.label || ''}`.trim();

    return this.formBuilder.group({
      field: [fieldKey, Validators.required],
      header: [header, Validators.required],
      dateFormatMode: [this.getInitialColumnDateFormatMode(groupBy, resolvedField)],
      customDateFormat: [this.getInitialCustomColumnDateFormat(groupBy)],
      args: this.buildFieldArgsGroup(fieldKey, groupBy?.args || {}),
    });
  }

  private buildFilterGroup(
    field: ScheduledReportFieldDefinition,
    filter?: Partial<ScheduledReportDefinitionFilter> | null,
  ): UntypedFormGroup {
    const preferredOperator = field.filterOperators[0] || 'equals';
    const type = this.getFieldType(filter?.field || field.key);
    return this.formBuilder.group({
      field: [filter?.field || field.key, Validators.required],
      operator: [filter?.operator || preferredOperator, Validators.required],
      valueText: [type === 'string' || type === 'enum' ? (Array.isArray(filter?.value) ? '' : (filter?.value || '')) : ''],
      valueNumber: [type === 'number' ? filter?.value ?? null : null],
      valueBoolean: [type === 'boolean'
        ? (filter?.value === true || filter?.value === 1
          ? 'true'
          : (filter?.value === false || filter?.value === 0 ? 'false' : ''))
        : ''],
      valueDate: [type === 'date' || type === 'datetime' ? this.toDateInputValue(filter?.value) : ''],
      multiValueText: [Array.isArray(filter?.value) ? filter?.value.join(', ') : ''],
      optionValues: [Array.isArray(filter?.value) ? filter?.value : []],
      valueMode: [filter?.valueMode === 'runtime' ? 'runtime' : 'saved'],
      parameterKey: [filter?.parameterKey || this.createFilterParameterKey()],
      args: this.buildFieldArgsGroup(filter?.field || field.key, filter?.args || {}),
    });
  }

  private buildTemplateSourceGroups(
    sources: Record<string, { field: string; args?: Record<string, any>; transform?: ScheduledReportOutputTransform | null }>,
  ): UntypedFormGroup[] {
    return Object.entries(sources || {}).map(([token, sourceConfig]) => this.buildTemplateSourceGroup(token, sourceConfig));
  }

  private buildTemplateSourceGroup(
    token = '',
    sourceConfig?: { field: string; args?: Record<string, any>; transform?: ScheduledReportOutputTransform | null },
  ): UntypedFormGroup {
    const resolvedField = sourceConfig?.field || this.getDefaultFieldForColumns()?.key || '';
    return this.formBuilder.group({
      token: [token, Validators.required],
      field: [resolvedField, Validators.required],
      transform: [sourceConfig?.transform || ''],
      args: this.buildFieldArgsGroup(resolvedField, sourceConfig?.args || {}),
    });
  }

  private buildFieldArgsGroup(fieldKey: string, args: Record<string, any> = {}): UntypedFormGroup {
    const group = this.formBuilder.group({});
    const handledArgKeys = new Set<string>();
    const providedArgKeys = new Set(Object.keys(args || {}));
    Object.entries(this.getFieldArgsSchema(fieldKey)).forEach(([argKey, schema]) => {
      if (!this.shouldRenderFieldArg(fieldKey, argKey)) {
        return;
      }
      if (this.isVendorSelectionArg(argKey, schema)) {
        const draft = this.hydrateVendorSelectionDraft(args);
        group.addControl('vendorSlugs', new UntypedFormControl(draft.vendorSlugs));
        group.addControl('productIds', new UntypedFormControl(draft.productIds));
        handledArgKeys.add(argKey);
        return;
      }

      const rawValue = Object.prototype.hasOwnProperty.call(args, argKey)
        ? args[argKey]
        : this.getDefaultArgValue(fieldKey, argKey, schema);
      group.addControl(argKey, new UntypedFormControl(rawValue));
      handledArgKeys.add(argKey);
    });

    Object.keys(args || {}).forEach((argKey) => {
      if (handledArgKeys.has(argKey) || group.contains(argKey)) {
        return;
      }
      group.addControl(argKey, new UntypedFormControl(args[argKey]));
    });
    this.initialArgKeysByGroup.set(group, providedArgKeys);
    return group;
  }

  private shouldRenderFieldArg(fieldKey: string, argKey: string): boolean {
    if (fieldKey === 'semantic.company_payroll_config_value'
      && (argKey === 'vendorProductScoped' || argKey === 'fallbackToSectionKey')) {
      return false;
    }
    return true;
  }

  private replaceFieldArgsGroup(control: AbstractControl, fieldKey: string, args: Record<string, any> = {}): void {
    (control as UntypedFormGroup).setControl('args', this.buildFieldArgsGroup(fieldKey, args));
  }

  private syncColumnDateFormatControls(control: AbstractControl, fieldKey: string): void {
    this.syncDateFormatControls(control, fieldKey);
  }

  private syncDateFormatControls(control: AbstractControl, fieldKey: string): void {
    const fieldType = this.getFieldType(fieldKey);
    if (fieldType !== 'date' && fieldType !== 'datetime') {
      control.get('dateFormatMode')?.setValue('datetime_timezone', { emitEvent: false });
      control.get('customDateFormat')?.setValue('', { emitEvent: false });
      return;
    }

    const currentMode = `${control.get('dateFormatMode')?.value || ''}`;
    if (!this.isDateFormatMode(currentMode)) {
      control.get('dateFormatMode')?.setValue(fieldType === 'date' ? 'date' : 'datetime_timezone', { emitEvent: false });
    }
  }

  private replaceAggregationCountDistinctArgsGroup(fieldKey: string, args: Record<string, any> = {}): void {
    this.aggregationGroup.setControl('countDistinctArgs', this.buildFieldArgsGroup(fieldKey, args));
  }

  private getDefaultFieldForColumns(): ScheduledReportFieldDefinition | null {
    const baseEntity = `${this.reportForm.get('baseEntity')?.value || 'users'}`;
    return this.fieldCatalog.find((candidate) => !candidate.supportedBaseEntities || candidate.supportedBaseEntities.includes(baseEntity))
      || this.fieldCatalog[0]
      || null;
  }

  private getDefaultCountDistinctFieldKey(): string {
    const baseEntity = `${this.reportForm.get('baseEntity')?.value || 'users'}`;
    const fields = this.fieldCatalog.filter((candidate) => !candidate.supportedBaseEntities || candidate.supportedBaseEntities.includes(baseEntity));
    const preferredKeys = [
      `${baseEntity}.id`,
      `${baseEntity}.userId`,
      'users.id',
      'teamusers.userId',
    ];
    return preferredKeys
      .map((key) => fields.find((field) => field.key === key))
      .find((field): field is ScheduledReportFieldDefinition => !!field)?.key || '';
  }

  private syncAggregationCountDistinctField(): void {
    const currentField = `${this.aggregationGroup.get('countDistinctField')?.value || ''}`.trim();
    if (currentField && this.isFieldAvailableForCurrentBaseEntity(currentField)) {
      return;
    }

    const nextField = this.getDefaultCountDistinctFieldKey();
    this.aggregationGroup.get('countDistinctField')?.setValue(nextField, { emitEvent: false });
    this.replaceAggregationCountDistinctArgsGroup(nextField);
  }

  private isFieldAvailableForCurrentBaseEntity(fieldKey: string): boolean {
    const field = this.getField(fieldKey);
    const baseEntity = `${this.reportForm.get('baseEntity')?.value || 'users'}`;
    return !!field && (!field.supportedBaseEntities || field.supportedBaseEntities.includes(baseEntity));
  }

  private reconcileFieldsForCurrentBaseEntity(): void {
    if (this.isTemplateReport) {
      return;
    }

    const removedFieldLabels = new Set<string>();
    const removeUnavailableControls = (
      controls: UntypedFormArray,
      getFieldKeys: (control: AbstractControl) => string[],
    ): void => {
      for (let index = controls.length - 1; index >= 0; index -= 1) {
        const fieldKeys = getFieldKeys(controls.at(index)).filter(Boolean);
        const unavailableFieldKeys = fieldKeys.filter((fieldKey) => !this.isFieldAvailableForCurrentBaseEntity(fieldKey));
        if (!unavailableFieldKeys.length) {
          continue;
        }
        unavailableFieldKeys.forEach((fieldKey) => removedFieldLabels.add(this.getFieldLabel(fieldKey)));
        controls.removeAt(index);
      }
    };

    removeUnavailableControls(this.columnsArray, (control) => {
      const kind = `${control.get('kind')?.value || 'field'}` as ReportColumnKind;
      if (kind === 'field') {
        return [`${control.get('field')?.value || ''}`];
      }
      if (kind === 'template') {
        return this.getColumnSourcesArray(control).controls
          .map((sourceControl) => `${sourceControl.get('field')?.value || ''}`);
      }
      return [];
    });
    removeUnavailableControls(this.groupByArray, (control) => [`${control.get('field')?.value || ''}`]);
    removeUnavailableControls(this.filtersArray, (control) => [`${control.get('field')?.value || ''}`]);
    removeUnavailableControls(this.sortArray, (control) => [`${control.get('field')?.value || ''}`]);

    if (!this.isAggregatedCustomReport && this.columnsArray.length === 0) {
      this.addColumn();
    }

    this.preview = null;
    this.previewSignature = '';
    this.previewError = '';
    this.clearEditorSensitivePreviewState();

    if (removedFieldLabels.size) {
      const labels = Array.from(removedFieldLabels).sort((left, right) => left.localeCompare(right));
      this.snackBar.open(
        `${labels.join(', ')} ${labels.length === 1 ? 'was' : 'were'} removed because ${labels.length === 1 ? 'it is' : 'they are'} unavailable for this dataset.`,
        'Dismiss',
        { duration: 6000 },
      );
    }
  }

  private collectUnavailableFieldMessages(messages: string[]): void {
    if (this.isTemplateReport) {
      return;
    }

    const unavailableFields = new Set<string>();
    const collect = (fieldKey: string): void => {
      if (fieldKey && !this.isFieldAvailableForCurrentBaseEntity(fieldKey)) {
        unavailableFields.add(fieldKey);
      }
    };

    this.columnsArray.controls.forEach((control) => {
      const kind = `${control.get('kind')?.value || 'field'}` as ReportColumnKind;
      if (kind === 'field') {
        collect(`${control.get('field')?.value || ''}`);
      } else if (kind === 'template') {
        this.getColumnSourcesArray(control).controls
          .forEach((sourceControl) => collect(`${sourceControl.get('field')?.value || ''}`));
      }
    });
    this.groupByArray.controls.forEach((control) => collect(`${control.get('field')?.value || ''}`));
    this.filtersArray.controls.forEach((control) => collect(`${control.get('field')?.value || ''}`));
    this.sortArray.controls.forEach((control) => collect(`${control.get('field')?.value || ''}`));
    if (this.isAggregatedCustomReport) {
      collect(`${this.aggregationGroup.get('countDistinctField')?.value || ''}`);
    }

    const baseEntity = `${this.reportForm.get('baseEntity')?.value || ''}`;
    const datasetLabel = this.datasetOptions.find((dataset) => dataset.key === baseEntity)?.label || baseEntity;
    unavailableFields.forEach((fieldKey) => {
      messages.push(`${this.getFieldLabel(fieldKey)} is unavailable for ${datasetLabel}. Remove it or choose another dataset.`);
    });
  }

  private hydrateVendorSelectionDraft(args: Record<string, any> = {}): VendorSelectionDraft {
    const vendorSelections = Array.isArray(args.vendorSelections) ? args.vendorSelections : [];
    const vendorSlugs = vendorSelections.map((selection: any) => `${selection?.vendorSlug || ''}`.trim()).filter(Boolean);
    const productIds = vendorSelections.reduce((selected: string[], selection: any) => {
      const matches = this.productOptions
        .filter((product) => product.vendorSlug === selection.vendorSlug && (selection.productSlugs || []).includes(product.productSlug))
        .map((product) => product.id);
      return selected.concat(matches);
    }, []);

    return {
      vendorSlugs: Array.from(new Set(vendorSlugs)),
      productIds: Array.from(new Set(productIds)),
    };
  }

  private getDefaultArgValue(fieldKey: string, argKey: string, schema: ScheduledReportFieldArgSchema): any {
    if (schema.default !== undefined) {
      return schema.default;
    }
    if (fieldKey === 'semantic.company_payroll_config_value') {
      if (argKey === 'section') {
        return 'enroll';
      }
      if (argKey === 'vendorProductScoped' || argKey === 'fallbackToSectionKey') {
        return true;
      }
    }
    if (schema.type === 'boolean') {
      return false;
    }
    if (schema.type.endsWith('[]')) {
      return [];
    }
    return '';
  }

  private replaceFormArray(array: UntypedFormArray, controls: UntypedFormGroup[]): void {
    while (array.length) {
      array.removeAt(0);
    }
    controls.forEach((control) => array.push(control));
  }

  private loadReportIntoEditor(report: ScheduledReport, duplicate: boolean): void {
    this.editorIncludeDemoUsers = false;
    this.viewMode = duplicate ? 'create' : 'edit';
    this.activeListTab = `${report.reportType}` as ReportTabType;
    this.editingReportId = duplicate ? '' : report.id;
    this.detailReport = duplicate ? this.detailReport : report;
    this.preview = null;
    this.previewSignature = '';
    this.previewError = '';
    this.clearEditorSensitivePreviewState();
    this.validationMessages = [];

    const deliveryConfig = (report.deliveryConfig || {}) as any;
    const templateArgs = (report.templateArgs || {}) as any;
    const definition = report.definitionJson || { columns: [], filters: [], sort: [], audience: {}, temporal: {} };
    const scheduleConfig = report.scheduleConfig || {} as any;
    const runtimeConfig = report.runtimeConfig || {};
    const aggregation = definition.aggregation || null;

    const templateVendorSelections = Array.isArray(templateArgs.vendorSelections) ? templateArgs.vendorSelections : [];
    const templateVendorSelection = templateVendorSelections[0] || null;

    this.reportForm.reset({
      name: duplicate ? `${report.name} Copy` : report.name,
      description: report.description || '',
      status: duplicate ? 'draft' : report.status,
      reportType: report.reportType,
      resultMode: aggregation?.groupBy?.length ? 'aggregated' : 'detail',
      scopeType: report.scopeType,
      scopeIds: this.normalizeScopeIds(report.scopeIds, report.scopeId),
      templateKey: report.templateKey || '',
      baseEntity: report.baseEntity || definition.baseEntity || 'users',
      outputFormat: report.outputFormat,
      fileNamePattern: report.fileNamePattern || '',
      showReportDates: runtimeConfig.showReportDates === true,
      timezone: report.timezone,
      scheduleType: report.scheduleType,
      scheduleConfig: {
        time: scheduleConfig.time || '08:00',
        daysOfWeek: scheduleConfig.daysOfWeek || [1],
        dayOfMonth: scheduleConfig.dayOfMonth || 1,
        windowMode: scheduleConfig.windowMode || 'previous_month',
        rollingDays: scheduleConfig.rollingDays || 7,
      },
      templateArgs: {
        vendorSlugs: templateVendorSelections.length
          ? templateVendorSelections.map((selection: any) => selection.vendorSlug).filter(Boolean)
          : (templateArgs.vendorSlug ? [templateArgs.vendorSlug] : this.getDefaultTemplateVendorSlugs()),
        vendorSlug: templateVendorSelection?.vendorSlug || templateArgs.vendorSlug || '',
        productSlugs: this.getTemplateProductIdsFromTemplateArgs(templateArgs),
        enrollmentStatusFilter: this.normalizeTemplateEnrollmentStatusFilter(templateArgs.enrollmentStatusFilter),
        includeUsersWithoutCurrentTeamMembership: templateArgs.includeUsersWithoutCurrentTeamMembership !== false,
      },
      customAudience: {
        includeUsersWithoutCurrentTeamMembership: definition.audience?.includeUsersWithoutCurrentTeamMembership !== false,
      },
      temporal: {
        asOfMode: definition.temporal?.asOfMode || 'run_time',
        subscriptionCoverageMode: definition.temporal?.subscriptionCoverageMode || 'window_overlap',
      },
      distinct: definition.distinct !== false,
      deliveryChannel: report.deliveryChannel,
      deliveryConfig: {
        host: deliveryConfig.host || '',
        port: deliveryConfig.port || 22,
        username: deliveryConfig.username || '',
        passwordSecretKey: deliveryConfig.passwordSecretKey || '',
        privateKeySecretKey: deliveryConfig.privateKeySecretKey || '',
        folder: deliveryConfig.folder || '',
        fileNamePrefix: deliveryConfig.fileNamePrefix || '',
        pgpEnabled: deliveryConfig.pgp?.enabled === true,
        publicKeySecretKey: deliveryConfig.pgp?.publicKeySecretKey || '',
        toText: (deliveryConfig.to || []).join(', '),
        ccText: (deliveryConfig.cc || []).join(', '),
        bccText: (deliveryConfig.bcc || []).join(', '),
        subjectTemplate: deliveryConfig.subjectTemplate || '',
        bodyTemplate: deliveryConfig.bodyTemplate || '',
      },
      aggregation: {
        countHeader: this.getAggregationCountHeader(aggregation),
        countDistinctField: aggregation?.countDistinct?.field || this.getDefaultCountDistinctFieldKey(),
        reportTotal: aggregation?.reportTotal === true,
      },
    }, { emitEvent: false });
    this.replaceAggregationCountDistinctArgsGroup(
      `${this.aggregationGroup.get('countDistinctField')?.value || ''}`,
      aggregation?.countDistinct?.args || {},
    );

    this.replaceFormArray(
      this.groupByArray,
      (aggregation?.groupBy || []).map((groupBy) => this.buildAggregationGroupByControl(null, groupBy)),
    );
    this.replaceFormArray(this.columnsArray, (definition.columns || []).map((column) => this.buildColumnGroup(null, 'field', column)));
    this.replaceFormArray(
      this.templateColumnsArray,
      this.getOrderedTemplateColumns(
        this.getTemplateByKey(report.templateKey),
        (templateArgs.columnOverrides || []) as ScheduledReportTemplateColumnOverride[],
      ).map((column) => this.buildTemplateColumnGroup(column)),
    );

    this.replaceFormArray(this.filtersArray, (definition.filters || []).map((filter) => {
      const fieldDefinition = this.getField(filter.field) || this.getDefaultFieldForColumns();
      return fieldDefinition ? this.buildFilterGroup(fieldDefinition, filter) : this.buildFilterGroup({
        key: filter.field,
        label: filter.field,
        group: 'Unknown',
        description: '',
        type: 'string',
        filterOperators: ['equals'],
        sortable: false,
      }, filter);
    }));

    this.replaceFormArray(
      this.sortArray,
      this.getInitialSortConfiguration(report, definition).map((sortItem) => this.buildSortGroup(sortItem)),
    );

    if (`${report.scopeType}` === 'team') {
      void this.ensureTeamOptionsLoaded().then(() => {
        this.ensureScopeSelection('team', true);
        this.updateDynamicValidators();
      });
    }
    this.ensureScopeSelection(`${report.scopeType}` as ScopeType, true);
    this.updateDynamicValidators();
  }

  private async persistReport(
    status: 'draft' | 'active' | 'paused',
    requirePreviewForActive = status === 'active',
  ): Promise<ScheduledReport | null> {
    this.validationMessages = this.collectValidationMessages(status === 'active' && requirePreviewForActive, status);
    if (this.validationMessages.length > 0) {
      this.markEditorTouched();
      return null;
    }

    let payload: ScheduledReportPayload;
    try {
      payload = this.buildPayload(status);
    } catch (error: any) {
      this.validationMessages = [error.message || 'Review the report configuration and try again.'];
      return null;
    }

    this.isSaving = true;
    try {
      const saved = this.editingReportId
        ? await this.scheduledReportsService.updateReport(this.editingReportId, payload)
        : await this.scheduledReportsService.createReport(payload);

      const saveMessage = status === 'active'
        ? 'Report activated.'
        : status === 'paused'
          ? 'Report saved as paused.'
          : 'Draft saved.';
      this.snackBar.open(saveMessage, 'Dismiss', { duration: 3000 });
      await this.reloadReports();
      this.editingReportId = saved.id;
      await this.openReport(saved);
      return saved;
    } catch (error: any) {
      this.presentError(error, 'Unable to save the report.');
      return null;
    } finally {
      this.isSaving = false;
    }
  }

  private buildPayload(status: string): ScheduledReportPayload {
    const raw = this.reportForm.getRawValue();
    const reportType = `${raw.reportType}` as ReportTabType;
    const deliveryChannel = `${raw.deliveryChannel}` as ScheduledReportDeliveryChannel;
    const scheduleConfigRaw = raw.scheduleConfig || {};
    const scheduleConfig: any = {
      time: scheduleConfigRaw.time,
      windowMode: scheduleConfigRaw.windowMode,
    };

    if (`${raw.scheduleType}` === 'weekly') {
      scheduleConfig.daysOfWeek = scheduleConfigRaw.daysOfWeek || [];
    }
    if (`${raw.scheduleType}` === 'monthly') {
      scheduleConfig.dayOfMonth = Number(scheduleConfigRaw.dayOfMonth || 1);
    }
    if (`${scheduleConfigRaw.windowMode}` === 'rolling_days') {
      scheduleConfig.rollingDays = Number(scheduleConfigRaw.rollingDays || 7);
    }

    const scopeIds = raw.scopeType === 'global' ? [] : this.normalizeScopeIds(raw.scopeIds);

    const payload: ScheduledReportPayload = {
      name: `${raw.name || ''}`.trim(),
      description: `${raw.description || ''}`.trim() || null,
      status,
      scopeType: `${raw.scopeType || ''}`,
      scopeIds,
      scopeId: raw.scopeType === 'global'
        ? null
        : (scopeIds.length === 1 ? scopeIds[0] : null),
      reportType,
      outputFormat: `${raw.outputFormat}` as ScheduledReportOutputFormat,
      fileNamePattern: `${raw.fileNamePattern || ''}`.trim() || null,
      timezone: `${raw.timezone || ''}`,
      scheduleType: `${raw.scheduleType}` as ScheduledReportScheduleType,
      scheduleConfig,
      deliveryChannel,
      deliveryConfig: this.buildDeliveryConfig(deliveryChannel),
    };

    if (reportType === 'template') {
      payload.templateKey = `${raw.templateKey || ''}`;
      payload.templateArgs = this.buildTemplateArgs();
      payload.baseEntity = this.selectedTemplate?.baseEntity || 'users';
      payload.definitionJson = this.buildTemplateDefinition();
    } else {
      payload.baseEntity = `${raw.baseEntity || 'users'}`;
      payload.definitionJson = this.buildCustomDefinition();
    }

    payload.runtimeConfig = {
      skipIfEmpty: false,
      storeArtifact: !this.isSensitiveReport(payload),
      artifactRetentionDays: 90,
      showReportDates: raw.showReportDates === true,
    };

    return payload;
  }

  private buildDeliveryConfig(deliveryChannel: ScheduledReportDeliveryChannel): Record<string, any> {
    const raw = this.reportForm.get('deliveryConfig')?.getRawValue() || {};
    if (deliveryChannel === 'ftp') {
      return {
        host: `${raw.host || ''}`.trim(),
        port: Number(raw.port || 22),
        username: `${raw.username || ''}`.trim(),
        passwordSecretKey: `${raw.passwordSecretKey || ''}`.trim() || null,
        privateKeySecretKey: `${raw.privateKeySecretKey || ''}`.trim() || null,
        folder: `${raw.folder || ''}`.trim() || null,
        fileNamePrefix: `${raw.fileNamePrefix || ''}`.trim() || null,
        pgp: {
          enabled: raw.pgpEnabled === true,
          publicKeySecretKey: raw.pgpEnabled ? (`${raw.publicKeySecretKey || ''}`.trim() || null) : null,
        },
      };
    }

    const to = this.parseEmailList(raw.toText);
    const cc = this.parseEmailList(raw.ccText);
    const bcc = this.parseEmailList(raw.bccText);
    const subjectTemplate = `${raw.subjectTemplate || ''}`.trim() || this.buildDefaultEmailSubject();
    return {
      to,
      cc,
      bcc,
      subjectTemplate,
      bodyTemplate: `${raw.bodyTemplate || ''}`.trim(),
      attachFile: true,
    };
  }

  private buildDefaultEmailSubject(): string {
    const reportName = `${this.reportForm.get('name')?.value || ''}`.trim();
    return reportName ? `Scheduled report: ${reportName}` : '';
  }

  private getDefaultFileNamePattern(reportName?: string | null): string {
    const normalizedName = `${reportName || this.reportForm?.get('name')?.value || 'Report Name'}`
      .trim()
      .replace(/[\\/]/g, '-');
    return `${normalizedName || 'Report Name'} - {{runDate:YYYYMMDD}}.{{extension}}`;
  }

  private createFilterParameterKey(): string {
    return `filter_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  private buildTemplateArgs(): Record<string, any> {
    const raw = this.reportForm.get('templateArgs')?.getRawValue() || {};
    const enrollmentStatusFilter = this.normalizeTemplateEnrollmentStatusFilter(raw.enrollmentStatusFilter);
    const args: Record<string, any> = {
      includeUsersWithoutCurrentTeamMembership: raw.includeUsersWithoutCurrentTeamMembership === true,
    };

    if (this.selectedTemplateSupportsEnrollmentStatusFilter) {
      args.enrollmentStatusFilter = enrollmentStatusFilter;
    }

    const vendorSlug = `${raw.vendorSlug || ''}`.trim();
    const selectedVendorSlugs = this.getSelectedTemplateVendorSlugs(raw.vendorSlugs);
    const selectedProductIds = this.normalizeStringArray(raw.productSlugs);
    if (this.selectedTemplate?.templateArgsSchema?.vendorSelections) {
      args.vendorSelections = selectedVendorSlugs
        .map((selectedVendor) => {
          const vendorProducts = this.getAvailableProductsForVendorSlugs([selectedVendor]);
          const selectedProducts = selectedProductIds.length
            ? vendorProducts.filter((product) => selectedProductIds.includes(product.id))
            : vendorProducts;

          return {
            vendorSlug: selectedVendor,
            productSlugs: Array.from(new Set(selectedProducts.map((product) => product.productSlug).filter(Boolean))),
          };
        })
        .filter((selection) => selection.vendorSlug && selection.productSlugs.length);

      const legacySelection = args.vendorSelections.length === 1 ? args.vendorSelections[0] : null;
      if (legacySelection) {
        args.vendorSlug = legacySelection.vendorSlug;
        args.productSlugs = legacySelection.productSlugs;
      }
    } else {
      if (this.selectedTemplate?.templateArgsSchema?.vendorSlug) {
        args.vendorSlug = vendorSlug;
      }
      if (this.selectedTemplate?.templateArgsSchema?.productSlugs) {
        args.productSlugs = selectedProductIds
          .map((productId) => this.productOptions.find((product) => product.id === productId)?.productSlug || '')
          .filter(Boolean);
      }
    }

    if (this.selectedTemplateSupportsColumnCustomization && this.templateColumnsArray.length) {
      args.columnOverrides = this.getSerializedTemplateColumnControls().map((control) => {
        const field = `${control.get('field')?.value || ''}`.trim();
        return {
          field,
          header: `${control.get('header')?.value || ''}`.trim(),
          ...(control.get('reportTotal')?.value === true && this.isReportTotalField(field)
            ? { reportTotal: true }
            : {}),
        };
      });
    }

    return args;
  }

  private buildTemplateDefinition(): ScheduledReport['definitionJson'] {
    const existingTemplateDefinition = this.getExistingTemplateDefinition();

    return {
      version: 1,
      baseEntity: this.selectedTemplate?.baseEntity || `${this.reportForm.get('baseEntity')?.value || 'users'}`,
      columns: [],
      filters: existingTemplateDefinition?.filters || [],
      sort: this.buildSortDefinition(),
    };
  }

  private buildCustomDefinition(): ScheduledReport['definitionJson'] {
    const raw = this.reportForm.getRawValue();
    const aggregation = this.isAggregatedCustomReport ? this.buildAggregationDefinition() : null;
    const columns = aggregation ? [] : this.getSerializedCustomColumnControls().map((control) => this.mapColumnControl(control));
    const filters = this.filtersArray.controls.map((control) => this.mapFilterControl(control));

    return {
      version: aggregation ? 3 : 2,
      baseEntity: `${raw.baseEntity || 'users'}`,
      temporal: {
        asOfMode: `${raw.temporal?.asOfMode || 'run_time'}` as ScheduledReportAsOfMode,
        ...(`${raw.baseEntity || 'users'}` === 'vendorSubscriptionCoverage'
          ? {
            subscriptionCoverageMode: `${raw.temporal?.subscriptionCoverageMode || 'window_overlap'}` as ScheduledReportSubscriptionCoverageMode,
          }
          : {}),
      },
      audience: {
        includeUsersWithoutCurrentTeamMembership: raw.customAudience?.includeUsersWithoutCurrentTeamMembership === true,
      },
      columns,
      filters,
      sort: this.buildSortDefinition(),
      distinct: raw.distinct !== false,
      aggregation,
    };
  }

  private getSerializedTemplateColumnControls(): AbstractControl[] {
    return this.filterReportDateOutputColumnControls(this.templateColumnsArray.controls);
  }

  private getSerializedCustomColumnControls(): AbstractControl[] {
    return this.filterReportDateOutputColumnControls(this.columnsArray.controls);
  }

  private filterReportDateOutputColumnControls(controls: AbstractControl[]): AbstractControl[] {
    if (this.shouldIncludeEditorReportDates()) {
      return controls;
    }

    return controls.filter((control) => !this.isReportDateOutputColumnControl(control));
  }

  private isReportDateOutputColumnControl(control: AbstractControl): boolean {
    const field = `${control.get('field')?.value || ''}`;
    const header = `${control.get('header')?.value || control.get('defaultHeader')?.value || ''}`;
    return this.isReportDateOutputColumn(field, header);
  }

  private isReportDateOutputColumn(field: string, header: string): boolean {
    const normalizedHeader = this.normalizeReportDateColumnIdentifier(header);
    if (this.reportDateOutputColumnHeaders.has(normalizedHeader)) {
      return true;
    }

    const normalizedField = this.normalizeReportDateFieldIdentifier(field);
    return this.reportDateOutputColumnFields.has(normalizedField);
  }

  private normalizeReportDateColumnIdentifier(value: string): string {
    return `${value || ''}`.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private normalizeReportDateFieldIdentifier(value: string): string {
    return `${value || ''}`.trim().toLowerCase().replace(/\[(\w+)\]/g, '.$1');
  }

  private buildAggregationDefinition(): ScheduledReportDefinitionAggregation {
    const countDistinct = this.buildAggregationCountDistinctDefinition();
    return {
      type: 'count',
      groupBy: this.groupByArray.controls.map((control) => this.mapAggregationGroupByControl(control)),
      ...(countDistinct ? { countDistinct } : {}),
      countHeader: this.getAggregationCountHeader(),
      ...(this.aggregationGroup.get('reportTotal')?.value === true ? { reportTotal: true } : {}),
    };
  }

  private buildAggregationCountDistinctDefinition(): { field: string; args?: Record<string, any> } | null {
    const field = `${this.aggregationGroup.get('countDistinctField')?.value || ''}`.trim();
    if (!field) {
      return null;
    }

    const args = this.serializeFieldArgs(field, this.aggregationCountDistinctArgsGroup);
    return {
      field,
      ...(Object.keys(args).length ? { args } : {}),
    };
  }

  private buildSortDefinition(): Array<{ field: string; direction: 'asc' | 'desc'; args?: Record<string, any> }> {
    return this.sortArray.controls.map((control) => {
      const field = `${control.get('field')?.value || ''}`.trim();
      const args = this.serializeFieldArgs(field, this.getArgsGroup(control));
      return {
        field,
        direction: this.normalizeSortDirection(control.get('direction')?.value),
        ...(Object.keys(args).length ? { args } : {}),
      };
    });
  }

  private mapColumnControl(control: AbstractControl): ScheduledReportDefinitionColumn {
    const kind = `${control.get('kind')?.value || 'field'}` as ReportColumnKind;
    const header = `${control.get('header')?.value || ''}`.trim();

    if (kind === 'value') {
      return {
        header,
        value: control.get('value')?.value ?? '',
      };
    }

    if (kind === 'template') {
      const sources = this.getColumnSourcesArray(control).controls.reduce((acc: Record<string, any>, sourceControl) => {
        const token = `${sourceControl.get('token')?.value || ''}`.trim();
        const field = `${sourceControl.get('field')?.value || ''}`.trim();
        if (!token || !field) {
          return acc;
        }
        const args = this.serializeFieldArgs(field, this.getArgsGroup(sourceControl));
        acc[token] = {
          field,
          ...(Object.keys(args).length ? { args } : {}),
          ...(sourceControl.get('transform')?.value ? { transform: sourceControl.get('transform')?.value } : {}),
        };
        return acc;
      }, {});

      return {
        header,
        template: `${control.get('template')?.value || ''}`,
        ...(Object.keys(sources).length ? { sources } : {}),
      };
    }

    const field = `${control.get('field')?.value || ''}`.trim();
    const args = this.serializeFieldArgs(field, this.getArgsGroup(control));
    const dateFormat = this.getDateFormatForColumnControl(control);
    return {
      field,
      header: header || this.getFieldLabel(field),
      ...(control.get('reportTotal')?.value === true ? { reportTotal: true } : {}),
      ...(Object.keys(args).length ? { args } : {}),
      ...(control.get('transform')?.value ? { transform: control.get('transform')?.value } : {}),
      ...(dateFormat ? dateFormat : {}),
    };
  }

  private getDateFormatForColumnControl(control: AbstractControl): { dateFormatMode: DateFormatMode; dateFormat: string } | null {
    if (!this.isDateFormatColumn(control)) {
      return null;
    }

    return this.getDateFormatForDateFieldControl(control);
  }

  private getDateFormatForDateFieldControl(control: AbstractControl): { dateFormatMode: DateFormatMode; dateFormat: string } | null {
    const mode = `${control.get('dateFormatMode')?.value || ''}`;
    const dateFormatMode = this.isDateFormatMode(mode) ? mode : 'datetime_timezone';
    const dateFormat = dateFormatMode === 'custom'
      ? `${control.get('customDateFormat')?.value || ''}`.trim()
      : this.getPresetDateFormat(dateFormatMode);
    return dateFormat ? { dateFormatMode, dateFormat } : null;
  }

  private getPresetDateFormat(mode: DateFormatMode): string {
    switch (mode) {
      case 'date':
        return 'MMM D, YYYY';
      case 'datetime':
        return 'MMM D, YYYY, h:mm:ss A';
      case 'datetime_timezone':
        return 'MMM D, YYYY, h:mm:ss A z';
      case 'custom':
      default:
        return '';
    }
  }

  private mapAggregationGroupByControl(control: AbstractControl): ScheduledReportDefinitionAggregationLevel {
    const field = `${control.get('field')?.value || ''}`.trim();
    const header = `${control.get('header')?.value || ''}`.trim();
    const args = this.serializeFieldArgs(field, this.getArgsGroup(control));
    const dateFormat = this.isDateFormatGroupBy(control) ? this.getDateFormatForDateFieldControl(control) : null;

    return {
      field,
      header: header || this.getFieldLabel(field),
      ...(Object.keys(args).length ? { args } : {}),
      ...(dateFormat ? dateFormat : {}),
    };
  }

  private mapFilterControl(control: AbstractControl): ScheduledReportDefinitionFilter {
    const field = `${control.get('field')?.value || ''}`;
    const operator = `${control.get('operator')?.value || 'equals'}`;
    const fieldType = this.getFieldType(field);
    const args = this.serializeFieldArgs(field, this.getArgsGroup(control));
    const valueMode = `${control.get('valueMode')?.value || 'saved'}`;
    const filter: ScheduledReportDefinitionFilter = {
      field,
      operator,
      value: this.getFilterValue(control, fieldType, operator),
    };

    if (valueMode === 'runtime') {
      filter.valueMode = 'runtime';
      filter.parameterKey = `${control.get('parameterKey')?.value || this.createFilterParameterKey()}`.trim();
      delete filter.value;
    } else if (!this.filterOperatorUsesValue(operator)) {
      delete filter.value;
    }

    if (Object.keys(args).length) {
      filter.args = args;
    }

    return filter;
  }

  private getFilterValue(control: AbstractControl, fieldType: string, operator: string): any {
    if (!this.filterOperatorUsesValue(operator)) {
      return undefined;
    }
    if (this.filterOperatorUsesMultipleValues(operator)) {
      if (this.isFiniteOptionField(`${control.get('field')?.value || ''}`)) {
        return this.normalizeStringArray(control.get('optionValues')?.value);
      }
      const raw = `${control.get('multiValueText')?.value || ''}`;
      return raw.split(/[\n,]/).map((item) => item.trim()).filter(Boolean);
    }

    if (fieldType === 'number') {
      return Number(control.get('valueNumber')?.value || 0);
    }
    if (fieldType === 'boolean') {
      return `${control.get('valueBoolean')?.value || ''}` === 'true' ? 1 : 0;
    }
    if (fieldType === 'date') {
      return `${control.get('valueDate')?.value || ''}`;
    }
    if (fieldType === 'datetime') {
      return this.normalizeDateTimeValue(`${control.get('valueDate')?.value || ''}`);
    }
    return `${control.get('valueText')?.value || ''}`.trim();
  }

  private serializeFieldArgs(fieldKey: string, argsGroup: UntypedFormGroup | null | undefined): Record<string, any> {
    if (!fieldKey || !argsGroup) {
      return {};
    }

    const serialized: Record<string, any> = {};
    const handledControlKeys = new Set<string>();
    const initialArgKeys = this.initialArgKeysByGroup.get(argsGroup) || new Set<string>();
    Object.entries(this.getFieldArgsSchema(fieldKey)).forEach(([argKey, schema]) => {
      if (this.isVendorSelectionArg(argKey, schema)) {
        const vendorSlugs = this.normalizeStringArray(argsGroup.get('vendorSlugs')?.value);
        const selectedProductIds = this.normalizeStringArray(argsGroup.get('productIds')?.value);
        const vendorSelections = vendorSlugs
          .map((vendorSlug) => {
            const productSlugs = this.productOptions
              .filter((product) => product.vendorSlug === vendorSlug && selectedProductIds.includes(product.id))
              .map((product) => product.productSlug);
            return {
              vendorSlug,
              productSlugs: Array.from(new Set(productSlugs)),
            };
          })
          .filter((selection) => selection.vendorSlug || selection.productSlugs.length);
        if (vendorSelections.length) {
          serialized[argKey] = vendorSelections;
        }
        handledControlKeys.add('vendorSlugs');
        handledControlKeys.add('productIds');
        return;
      }

      const control = argsGroup.get(argKey);
      const value = control?.value;
      if (value === '' || value === null || value === undefined) {
        handledControlKeys.add(argKey);
        return;
      }
      if (schema.type === 'boolean'
        && value === false
        && !schema.required
        && !initialArgKeys.has(argKey)
        && !control?.dirty) {
        handledControlKeys.add(argKey);
        return;
      }
      if (Array.isArray(value) && !value.length) {
        handledControlKeys.add(argKey);
        return;
      }
      serialized[argKey] = value;
      handledControlKeys.add(argKey);
    });

    Object.keys(argsGroup.controls).forEach((controlKey) => {
      if (handledControlKeys.has(controlKey)) {
        return;
      }
      const value = argsGroup.get(controlKey)?.value;
      if (value === '' || value === null || value === undefined) {
        return;
      }
      if (Array.isArray(value) && !value.length) {
        return;
      }
      serialized[controlKey] = value;
    });
    return serialized;
  }

  private collectFieldArgMessages(fieldKey: string, argsGroup: UntypedFormGroup | null | undefined, label: string): string[] {
    if (!fieldKey || !this.fieldRequiresArgs(fieldKey) || !argsGroup) {
      return [];
    }

    const args = this.serializeFieldArgs(fieldKey, argsGroup);
    const messages: string[] = [];
    this.getFieldArgEntries(fieldKey).forEach(({ key, schema }) => {
      if (!schema.required) {
        return;
      }
      const value = args[key];
      const isMissing = value === undefined
        || value === null
        || value === ''
        || (Array.isArray(value) && value.length === 0);
      if (!isMissing) {
        return;
      }
      if (fieldKey === 'semantic.company_payroll_config_value' && key === 'key') {
        messages.push('Company Payroll Config Value requires args.key.');
        return;
      }
      if (fieldKey === 'semantic.partner_config_flag' && key === 'path') {
        messages.push('Partner Config Flag requires args.path.');
        return;
      }
      if (fieldKey === 'semantic.vendor_subscription_state_as_of' && key === 'vendorSelections') {
        messages.push(`${label} requires at least one vendor/product selection.`);
        return;
      }
      messages.push(`${label} requires ${this.getFieldArgLabel(key, schema)}.`);
    });
    return messages;
  }

  private collectValidationMessages(requirePreview: boolean, targetStatus?: string): string[] {
    const messages: string[] = [];
    const raw = this.reportForm.getRawValue();

    if (!`${raw.name || ''}`.trim()) {
      messages.push('Report name is required.');
    }
    if (raw.scopeType !== 'global' && !this.normalizeScopeIds(raw.scopeIds).length) {
      messages.push('Choose a report scope.');
    }
    if (!`${raw.fileNamePattern || ''}`.trim()) {
      messages.push('Enter a file name pattern.');
    }
    if (/[\\/]/.test(`${raw.fileNamePattern || ''}`)) {
      messages.push('File name pattern cannot include folder separators.');
    }
    if (!`${raw.scheduleConfig?.time || ''}`.trim()) {
      messages.push('Choose a time of day.');
    }
    if (!`${raw.scheduleConfig?.windowMode || ''}`.trim()) {
      messages.push('Choose a window mode.');
    }
    if (this.isWeeklySchedule && !(raw.scheduleConfig?.daysOfWeek || []).length) {
      messages.push('Choose at least one day of week for a weekly schedule.');
    }
    if (this.isMonthlySchedule && !(Number(raw.scheduleConfig?.dayOfMonth || 0) >= 1)) {
      messages.push('Choose a valid day of month.');
    }
    if (this.isRollingWindow && !(Number(raw.scheduleConfig?.rollingDays || 0) >= 1)) {
      messages.push('Enter the number of trailing days for a rolling window.');
    }

    if (this.isTemplateReport) {
      if (!`${raw.templateKey || ''}`.trim()) {
        messages.push('Choose a canned report template.');
      }
      const templateArgs = this.buildTemplateArgs();
      if (this.selectedTemplateNeedsVendorSelection && !(templateArgs.vendorSelections || []).length) {
        messages.push(
          this.getSelectedTemplateVendorSlugs(raw.templateArgs?.vendorSlugs).length
            ? 'Selected vendors do not have any available products.'
            : 'Subscription Status requires at least one vendor selection.',
        );
      }
      if (this.selectedTemplateSupportsColumnCustomization) {
        if (!this.templateColumnsArray.length) {
          messages.push('Choose at least one canned report output column.');
        }
        this.templateColumnsArray.controls.forEach((control) => {
          const field = `${control.get('field')?.value || ''}`;
          if (!`${control.get('header')?.value || ''}`.trim()) {
            messages.push(`Provide a column label for ${this.getTemplateColumnFieldLabel(field)}.`);
          }
          if (control.get('reportTotal')?.value === true && !this.isReportTotalField(field)) {
            messages.push(`Report total can only be enabled for numeric fields. Remove it from ${control.get('header')?.value || this.getTemplateColumnFieldLabel(field)}.`);
          }
        });
        const duplicateHeaders = this.findDuplicateHeaders(this.templateColumnsArray.controls);
        if (duplicateHeaders.length) {
          messages.push(`Output column headers must be unique. Duplicates: ${duplicateHeaders.join(', ')}.`);
        }
      }
    } else {
      if (!`${raw.baseEntity || ''}`.trim()) {
        messages.push('Choose a base dataset.');
      }
      if (this.isAggregatedCustomReport) {
        if (this.groupByArray.length === 0) {
          messages.push('Choose at least one group-by level.');
        }
        const countDistinctField = `${raw.aggregation?.countDistinctField || ''}`.trim();
        if (raw.distinct !== false && !countDistinctField) {
          messages.push('Choose a count distinct field for grouped counts.');
        }
        this.collectFieldArgMessages(
          countDistinctField,
          this.aggregationCountDistinctArgsGroup,
          countDistinctField ? this.getFieldLabel(countDistinctField) : 'count distinct field',
        ).forEach((message) => messages.push(message));
        const seenGroupByFields = new Set<string>();
        this.groupByArray.controls.forEach((control, index) => {
          const field = `${control.get('field')?.value || ''}`.trim();
          const header = `${control.get('header')?.value || ''}`.trim();
          if (!field) {
            messages.push(`Choose a field for group-by level ${index + 1}.`);
          }
          if (!header) {
            messages.push(`Provide a column label for group-by level ${index + 1}.`);
          }
          if (field && seenGroupByFields.has(field)) {
            messages.push(`Group-by field ${this.getFieldLabel(field)} can only be used once.`);
          }
          if (field) {
            seenGroupByFields.add(field);
          }
          if (this.isCustomDateFormatGroupBy(control) && !`${control.get('customDateFormat')?.value || ''}`.trim()) {
            messages.push(`Choose a custom date format for ${header || this.getFieldLabel(field)}.`);
          }
          this.collectFieldArgMessages(field, this.getArgsGroup(control), header || this.getFieldLabel(field)).forEach((message) => messages.push(message));
        });

        const duplicateHeaders = this.findDuplicateStrings(this.getAggregationOutputHeaders());
        if (duplicateHeaders.length) {
          messages.push(`Output column headers must be unique. Duplicates: ${duplicateHeaders.join(', ')}.`);
        }
      } else {
        if (this.columnsArray.length === 0) {
          messages.push('Choose at least one output column.');
        }
        this.columnsArray.controls.forEach((control) => {
          const kind = `${control.get('kind')?.value || 'field'}` as ReportColumnKind;
          const field = `${control.get('field')?.value || ''}`;
          const header = `${control.get('header')?.value || ''}`.trim();
          if (!header) {
            messages.push(`Provide a column label for ${this.getFieldLabel(field) || 'the output column'}.`);
          }
          if (kind === 'field') {
            if (!field) {
              messages.push('Each field column must choose a source field.');
            }
            if (control.get('reportTotal')?.value === true && !this.isReportTotalField(field)) {
              messages.push(`Report total can only be enabled for numeric fields. Remove it from ${header || this.getFieldLabel(field)}.`);
            }
            if (this.isCustomDateFormatColumn(control) && !`${control.get('customDateFormat')?.value || ''}`.trim()) {
              messages.push(`Choose a custom date format for ${header || this.getFieldLabel(field)}.`);
            }
            this.collectFieldArgMessages(field, this.getArgsGroup(control), this.getFieldLabel(field)).forEach((message) => messages.push(message));
          } else if (kind === 'template') {
            if (!`${control.get('template')?.value || ''}`.trim()) {
              messages.push(`Template column ${header || 'Column'} requires a template.`);
            }
            const seenTokens = new Set<string>();
            this.getColumnSourcesArray(control).controls.forEach((sourceControl) => {
              const token = `${sourceControl.get('token')?.value || ''}`.trim();
              const sourceField = `${sourceControl.get('field')?.value || ''}`.trim();
              if (!token) {
                messages.push(`Template column ${header || 'Column'} has a blank source token.`);
              } else if (seenTokens.has(token)) {
                messages.push(`Template column ${header || 'Column'} cannot repeat source token ${token}.`);
              } else {
                seenTokens.add(token);
              }
              if (!sourceField) {
                messages.push(`Template column ${header || 'Column'} requires a source field for each token.`);
              }
              this.collectFieldArgMessages(
                sourceField,
                this.getArgsGroup(sourceControl),
                `${header || 'Column'} source ${token || 'token'}`,
              ).forEach((message) => messages.push(message));
            });
          } else if (kind === 'value' && control.get('value')?.value === undefined) {
            messages.push(`Static value column ${header || 'Column'} requires a value.`);
          }
          if (kind !== 'field' && control.get('reportTotal')?.value === true) {
            messages.push(`Report total can only be enabled for field columns. Remove it from ${header || 'Column'}.`);
          }
        });
        const duplicateHeaders = this.findDuplicateHeaders(this.columnsArray.controls);
        if (duplicateHeaders.length) {
          messages.push(`Output column headers must be unique. Duplicates: ${duplicateHeaders.join(', ')}.`);
        }
      }
      this.filtersArray.controls.forEach((control) => {
        const field = `${control.get('field')?.value || ''}`;
        const operator = `${control.get('operator')?.value || 'equals'}`;
        const isRuntime = this.isRuntimeFilter(control);
        const value = this.getFilterValue(control, this.getFieldType(field), operator);
        if (targetStatus === 'active' && isRuntime) {
          messages.push(`Save a concrete value for ${this.getFieldLabel(field)} before activating this scheduled report.`);
        }
        if (!isRuntime && this.filterOperatorUsesMultipleValues(operator) && (!Array.isArray(value) || !value.length)) {
          messages.push(`Add at least one value for ${this.getFieldLabel(field)}.`);
        }
        if (!isRuntime
          && this.filterOperatorUsesValue(operator)
          && !this.filterOperatorUsesMultipleValues(operator)
          && (value === '' || value === null || value === undefined)) {
          messages.push(`Choose a value for ${this.getFieldLabel(field)}.`);
        }
        this.collectFieldArgMessages(field, this.getArgsGroup(control), this.getFieldLabel(field)).forEach((message) => messages.push(message));
      });
      this.collectUnavailableFieldMessages(messages);
    }

    this.sortArray.controls.forEach((control, index) => {
      const field = `${control.get('field')?.value || ''}`.trim();
      if (!field) {
        messages.push(`Choose a sort field for row ${index + 1}.`);
      } else if (this.isAggregatedCustomReport && !this.getAggregationGroupByFieldKeys().has(field)) {
        messages.push(`Aggregated reports can only sort by grouped fields. Remove ${this.getFieldLabel(field)} from sort row ${index + 1}.`);
      }
      this.collectFieldArgMessages(field, this.getArgsGroup(control), `sort row ${index + 1}`).forEach((message) => messages.push(message));
    });

    const deliveryConfig = this.buildDeliveryConfig(`${raw.deliveryChannel}` as ScheduledReportDeliveryChannel);
    if (`${raw.deliveryChannel}` === 'ftp') {
      if (!`${deliveryConfig.host || ''}`.trim()) {
        messages.push('SFTP delivery requires a host.');
      }
      if (!`${deliveryConfig.username || ''}`.trim()) {
        messages.push('SFTP delivery requires a username.');
      }
    }
    if (`${raw.deliveryChannel}` === 'email' && !(deliveryConfig.to || []).length) {
      messages.push('Email delivery requires at least one recipient.');
    }
    if (targetStatus && targetStatus !== 'draft') {
      try {
        const payload = this.buildPayload(targetStatus);
        if (this.isSensitiveReport(payload) && `${raw.deliveryChannel}` !== 'ftp') {
          messages.push('Reports containing full SSNs require SFTP delivery.');
        }
      } catch (error: any) {
        messages.push(error?.message || 'Review the report configuration and try again.');
      }
    }
    if (requirePreview && !this.previewIsCurrent) {
      messages.push('Preview the report successfully before activating it.');
    }

    return Array.from(new Set(messages));
  }

  private normalizeTemplateEnrollmentStatusFilter(value: any): TemplateEnrollmentStatusFilter {
    return value === 'enrolled' || value === 'not_enrolled' ? value : 'all';
  }

  private syncTemplateColumns(
    template: ScheduledReportTemplate | null,
    columnOverrides?: ScheduledReportTemplateColumnOverride[] | null,
  ): void {
    if (!template?.supportsColumnCustomization) {
      this.replaceFormArray(this.templateColumnsArray, []);
      return;
    }

    this.replaceFormArray(
      this.templateColumnsArray,
      this.getOrderedTemplateColumns(template, columnOverrides).map((column) => this.buildTemplateColumnGroup(column)),
    );
  }

  private syncTemplateSort(
    template: ScheduledReportTemplate | null,
    sortOverrides?: Array<{ field: string; direction: 'asc' | 'desc' }> | null,
  ): void {
    if (!template?.supportsSortCustomization) {
      this.replaceFormArray(this.sortArray, []);
      return;
    }

    this.replaceFormArray(
      this.sortArray,
      this.getOrderedTemplateSort(template, sortOverrides).map((sortItem) => this.buildSortGroup(sortItem)),
    );
  }

  private getTemplateByKey(templateKey?: string | null): ScheduledReportTemplate | null {
    if (!templateKey) {
      return null;
    }

    return this.templates.find((template) => template.key === templateKey) || null;
  }

  private getSortedTemplateDefaultColumns(template: ScheduledReportTemplate | null): ScheduledReportTemplateColumn[] {
    return (template?.defaultColumns || [])
      .map((column, index) => ({ column, index }))
      .sort((left, right) => {
        const leftOrder = Number.isFinite(left.column.defaultOrder) ? Number(left.column.defaultOrder) : Number.MAX_SAFE_INTEGER;
        const rightOrder = Number.isFinite(right.column.defaultOrder) ? Number(right.column.defaultOrder) : Number.MAX_SAFE_INTEGER;
        if (leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }
        return left.index - right.index;
      })
      .map((item) => item.column);
  }

  private getSortedTemplateDefaultSort(template: ScheduledReportTemplate | null): ScheduledReportTemplateSort[] {
    return (template?.defaultSort || [])
      .map((sortItem, index) => ({ sortItem, index }))
      .sort((left, right) => {
        const leftOrder = Number.isFinite(left.sortItem.defaultOrder) ? Number(left.sortItem.defaultOrder) : Number.MAX_SAFE_INTEGER;
        const rightOrder = Number.isFinite(right.sortItem.defaultOrder) ? Number(right.sortItem.defaultOrder) : Number.MAX_SAFE_INTEGER;
        if (leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }
        return left.index - right.index;
      })
      .map((item) => item.sortItem);
  }

  private getOrderedTemplateColumns(
    template: ScheduledReportTemplate | null,
    columnOverrides?: ScheduledReportTemplateColumnOverride[] | null,
  ): ScheduledReportTemplateColumn[] {
    const defaultColumns = this.getSortedTemplateDefaultColumns(template);
    if (!defaultColumns.length) {
      return [];
    }

    const defaultColumnsByField = new Map(defaultColumns.map((column) => [column.field, column]));
    const orderedColumns: ScheduledReportTemplateColumn[] = [];
    const includedFields = new Set<string>();

    (columnOverrides || []).forEach((override) => {
      const field = `${override?.field || ''}`.trim();
      const baseColumn = defaultColumnsByField.get(field);
      if (!baseColumn || includedFields.has(field)) {
        return;
      }

      orderedColumns.push({
        ...baseColumn,
        header: `${override?.header || ''}`.trim() || baseColumn.header,
        ...(override?.reportTotal === true ? { reportTotal: true } : {}),
      });
      includedFields.add(field);
    });

    defaultColumns.forEach((column) => {
      if (includedFields.has(column.field)) {
        return;
      }
      orderedColumns.push(column);
    });

    return orderedColumns;
  }

  private getOrderedTemplateSort(
    template: ScheduledReportTemplate | null,
    sortOverrides?: ScheduledReportDefinitionSort[] | null,
  ): ScheduledReportDefinitionSort[] {
    if ((sortOverrides || []).length) {
      return (sortOverrides || [])
        .filter((sortItem) => `${sortItem?.field || ''}`.trim())
        .map((sortItem) => ({
          field: `${sortItem.field || ''}`.trim(),
          direction: this.normalizeSortDirection(sortItem.direction),
          ...(sortItem.args ? { args: { ...sortItem.args } } : {}),
        }));
    }

    return this.getSortedTemplateDefaultSort(template).map((sortItem) => ({
      field: sortItem.field,
      direction: this.normalizeSortDirection(sortItem.direction),
    }));
  }

  private getInitialSortConfiguration(
    report: ScheduledReport,
    definition: ScheduledReport['definitionJson'],
  ): ScheduledReportDefinitionSort[] {
    const configuredSort: ScheduledReportDefinitionSort[] = (definition.sort || []).map((sortItem) => ({
      field: `${sortItem?.field || ''}`.trim(),
      direction: this.normalizeSortDirection(sortItem?.direction),
      ...(sortItem?.args ? { args: { ...sortItem.args } } : {}),
    })).filter((sortItem) => sortItem.field);

    if (configuredSort.length) {
      return configuredSort;
    }

    if (`${report.reportType}` !== 'template') {
      return [];
    }

    return this.getOrderedTemplateSort(this.getTemplateByKey(report.templateKey));
  }

  private normalizeSortDirection(direction: any): 'asc' | 'desc' {
    return `${direction || 'asc'}`.toLowerCase() === 'desc' ? 'desc' : 'asc';
  }

  private getExistingTemplateDefinition(): ScheduledReport['definitionJson'] | null {
    if (!this.detailReport || `${this.detailReport.reportType}` !== 'template') {
      return null;
    }

    const currentTemplateKey = `${this.reportForm.get('templateKey')?.value || ''}`;
    if (`${this.detailReport.templateKey || ''}` !== currentTemplateKey) {
      return null;
    }

    return this.detailReport.definitionJson || null;
  }

  private getTemplateSortableFields(): ScheduledReportFieldDefinition[] {
    const template = this.selectedTemplate;
    const fieldOrder = new Map<string, number>();
    let nextOrder = 0;
    this.getSortedTemplateDefaultSort(template).forEach((sortItem) => {
      if (!fieldOrder.has(sortItem.field)) {
        fieldOrder.set(sortItem.field, nextOrder++);
      }
    });
    this.getSortedTemplateDefaultColumns(template).forEach((column) => {
      if (!fieldOrder.has(column.field)) {
        fieldOrder.set(column.field, nextOrder++);
      }
    });
    this.sortArray.controls.forEach((control) => {
      const field = `${control.get('field')?.value || ''}`.trim();
      if (field && !fieldOrder.has(field)) {
        fieldOrder.set(field, nextOrder++);
      }
    });

    return Array.from(fieldOrder.keys())
      .map((fieldKey) => this.getField(fieldKey))
      .filter((field): field is ScheduledReportFieldDefinition => !!field && field.sortable)
      .sort((left, right) => {
        const leftOrder = fieldOrder.get(left.key) ?? Number.MAX_SAFE_INTEGER;
        const rightOrder = fieldOrder.get(right.key) ?? Number.MAX_SAFE_INTEGER;
        if (leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }
        return left.label.localeCompare(right.label);
      });
  }

  private findDuplicateHeaders(controls: AbstractControl[]): string[] {
    return this.findDuplicateStrings(controls.map((control) => `${control.get('header')?.value || ''}`.trim()));
  }

  private findDuplicateStrings(values: string[]): string[] {
    const counts = new Map<string, number>();
    values
      .map((value) => `${value || ''}`.trim())
      .filter(Boolean)
      .forEach((value) => {
        counts.set(value, (counts.get(value) || 0) + 1);
      });

    return Array.from(counts.entries())
      .filter(([, count]) => count > 1)
      .map(([value]) => value);
  }

  private normalizeScopeIds(scopeIds: any, fallbackScopeId?: string | null): string[] {
    const normalized = (Array.isArray(scopeIds) ? scopeIds : (scopeIds ? [scopeIds] : []))
      .map((value) => `${value || ''}`.trim())
      .filter(Boolean);

    if (normalized.length) {
      return Array.from(new Set(normalized));
    }

    return `${fallbackScopeId || ''}`.trim() ? [`${fallbackScopeId}`.trim()] : [];
  }

  private normalizeStringArray(values: any): string[] {
    return (Array.isArray(values) ? values : (values ? [values] : []))
      .map((value) => `${value || ''}`.trim())
      .filter(Boolean);
  }

  private getDefaultTemplateVendorSlugs(): string[] {
    return this.vendorOptions.map((vendor) => vendor.value);
  }

  private getSelectedTemplateVendorSlugs(rawVendorSlugs?: any): string[] {
    return this.normalizeStringArray(rawVendorSlugs !== undefined ? rawVendorSlugs : this.templateArgsGroup.get('vendorSlugs')?.value)
      .filter((vendorSlug) => vendorSlug !== this.allTemplateVendorsValue);
  }

  private getAggregationGroupByFieldKeys(): Set<string> {
    return new Set(
      this.groupByArray.controls
        .map((control) => `${control.get('field')?.value || ''}`.trim())
        .filter(Boolean),
    );
  }

  private getAggregationCountHeader(aggregation?: ScheduledReportDefinitionAggregation | null): string {
    const countHeader = aggregation?.countHeader !== undefined
      ? aggregation.countHeader
      : this.reportForm.get('aggregation.countHeader')?.value;
    return `${countHeader || ''}`.trim() || 'Count';
  }

  private getAggregationOutputHeaders(): string[] {
    return [
      ...this.groupByArray.controls.map((control) => `${control.get('header')?.value || this.getFieldLabel(`${control.get('field')?.value || ''}`)}`.trim()),
      this.getAggregationCountHeader(),
    ].filter(Boolean);
  }

  private getAvailableProductsForVendorSlugs(vendorSlugs: string[]): ProductOption[] {
    if (!vendorSlugs.length) {
      return [];
    }

    const selectedVendorSlugs = new Set(vendorSlugs);
    return this.productOptions.filter((product) => selectedVendorSlugs.has(product.vendorSlug));
  }

  private getTemplateProductIdsFromTemplateArgs(templateArgs: Record<string, any>): string[] {
    const vendorSelections = Array.isArray(templateArgs.vendorSelections) ? templateArgs.vendorSelections : [];
    if (vendorSelections.length) {
      const selectedVendorSlugs = vendorSelections.map((selection: any) => selection.vendorSlug).filter(Boolean);
      const allAvailableProducts = this.getAvailableProductsForVendorSlugs(selectedVendorSlugs);
      const selectedProductIds = vendorSelections.reduce((productIds: string[], selection: any) => {
        const matchingProductIds = this.productOptions
          .filter((product) => product.vendorSlug === selection.vendorSlug && (selection.productSlugs || []).includes(product.productSlug))
          .map((product) => product.id);
        return productIds.concat(matchingProductIds);
      }, []);

      if (allAvailableProducts.length && selectedProductIds.length === allAvailableProducts.length) {
        return [];
      }

      return selectedProductIds;
    }

    if (!templateArgs.vendorSlug || !Array.isArray(templateArgs.productSlugs)) {
      return [];
    }

    const selectedProductIds = this.productOptions
      .filter((product) => product.vendorSlug === templateArgs.vendorSlug && templateArgs.productSlugs.includes(product.productSlug))
      .map((product) => product.id);

    return selectedProductIds.length === this.getAvailableProducts(templateArgs.vendorSlug).length ? [] : selectedProductIds;
  }

  private formatScopeLabels(scopeType: string, scopeIds: string[]): string {
    if (scopeType === 'global') {
      return 'Global';
    }

    const options = this.getScopeOptions(scopeType);
    const labels = scopeIds
      .map((scopeId) => options.find((scope) => scope.id === scopeId)?.label || scopeId)
      .filter(Boolean);

    if (!labels.length) {
      return '--';
    }

    if (labels.length <= 2) {
      return labels.join(', ');
    }

    return `${labels.slice(0, 2).join(', ')} +${labels.length - 2} more`;
  }

  private buildPreviewSignature(payload: ScheduledReportPayload): string {
    return JSON.stringify({ payload, includeDemoUsers: this.editorIncludeDemoUsers });
  }

  private applyTemplateVendorSelection(vendorSlugs: string[]): void {
    const vendorSlugsControl = this.templateArgsGroup.get('vendorSlugs');
    vendorSlugsControl?.setValue(vendorSlugs, { emitEvent: false });
    this.templateArgsGroup.get('vendorSlug')?.setValue(vendorSlugs[0] || '', { emitEvent: false });

    const validProductIds = new Set(this.getAvailableProductsForVendorSlugs(vendorSlugs).map((product) => product.id));
    const selectedProductIds = this.normalizeStringArray(this.templateArgsGroup.get('productSlugs')?.value);
    this.templateArgsGroup.get('productSlugs')?.setValue(
      selectedProductIds.filter((productId) => validProductIds.has(productId)),
      { emitEvent: false },
    );
  }

  private markEditorTouched(): void {
    this.reportForm.markAllAsTouched();
    [
      ...this.groupByArray.controls,
      ...this.templateColumnsArray.controls,
      ...this.columnsArray.controls,
      ...this.filtersArray.controls,
      ...this.sortArray.controls,
    ].forEach((control) => control.markAllAsTouched());
  }

  private parseEmailList(value: string): string[] {
    return `${value || ''}`
      .split(/[\n,;]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  private replaceReport(report: ScheduledReport): void {
    const index = this.reports.findIndex((item) => item.id === report.id);
    if (index === -1) {
      this.reports = [report, ...this.reports];
      return;
    }
    const next = [...this.reports];
    next[index] = report;
    this.reports = next;
  }

  private toDateInputValue(value: any): string {
    if (!value) {
      return '';
    }
    const text = `${value}`;
    return text.includes('T') ? text : text.replace(' ', 'T').slice(0, 16);
  }

  private normalizeDateTimeValue(value: string): string {
    if (!value) {
      return '';
    }
    if (value.includes('T')) {
      return `${value.replace('T', ' ')}:00`;
    }
    return value;
  }

  private getFallbackRunContext(): { asOfAt: string; windowStart: string; windowEnd: string } {
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - 29);
    return {
      asOfAt: now.toISOString(),
      windowStart: start.toISOString(),
      windowEnd: now.toISOString(),
    };
  }

  private formatPatternDate(date: Date, format: string): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    switch (format) {
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      case 'YYYYMMDD':
      default:
        return `${year}${month}${day}`;
    }
  }

  private formatPreviewDateValue(value: any): string | null {
    const parsedDate = this.parsePreviewDateValue(value);
    if (!parsedDate) {
      return null;
    }

    return new Intl.DateTimeFormat('en-US', {
      timeZone: this.getPreviewTimezone(),
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    }).format(parsedDate);
  }

  private parsePreviewDateValue(value: any): Date | null {
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    if (!trimmed || !this.looksLikeDateTimeValue(trimmed)) {
      return null;
    }

    const parsedDate = new Date(trimmed);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  private looksLikeDateTimeValue(value: string): boolean {
    return /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:?\d{2})?$/.test(value);
  }

  private getPreviewTimezone(): string {
    return this.preview?.runContext?.timezone
      || this.detailPreview?.runContext?.timezone
      || `${this.reportForm.get('timezone')?.value || this.detailReport?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles'}`;
  }

  private compareReportListItems(left: ScheduledReport, right: ScheduledReport): number {
    const direction = this.reportListSort.direction === 'asc' ? 1 : -1;
    const activeColumn = this.reportListSort.active as ReportListSortColumn;
    const leftValue = this.getReportListSortValue(left, activeColumn);
    const rightValue = this.getReportListSortValue(right, activeColumn);
    const comparison = this.compareNullableSortValues(leftValue, rightValue);
    if (comparison !== 0) {
      return comparison * direction;
    }
    return `${left.name || ''}`.localeCompare(`${right.name || ''}`, undefined, { sensitivity: 'base', numeric: true });
  }

  private getReportListSortValue(report: ScheduledReport, column: ReportListSortColumn): string | number | null {
    switch (column) {
      case 'name':
        return `${report.name || ''}`;
      case 'reportType':
        return this.getReportTypeLabel(report.reportType);
      case 'scope':
        return this.getScopeLabel(report);
      case 'outputFormat':
        return this.getFormatLabel(report.outputFormat);
      case 'deliveryChannel':
        return this.getDeliveryLabel(report.deliveryChannel);
      case 'schedule':
        return this.getScheduleSummary(report);
      case 'status':
        return `${report.status || ''}`;
      case 'lastRunAt':
        return this.toSortableTimestamp(report.lastRunAt);
      case 'lastResult':
        return this.getLastResultLabel(report);
      case 'nextRunAt':
        return this.toSortableTimestamp(report.nextRunAt);
      default:
        return null;
    }
  }

  private toSortableTimestamp(value?: string | null): number | null {
    if (!value) {
      return null;
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
  }

  private compareNullableSortValues(left: string | number | null, right: string | number | null): number {
    const leftMissing = left === null || left === undefined || left === '';
    const rightMissing = right === null || right === undefined || right === '';
    if (leftMissing && rightMissing) {
      return 0;
    }
    if (leftMissing) {
      return 1;
    }
    if (rightMissing) {
      return -1;
    }
    if (typeof left === 'number' && typeof right === 'number') {
      return left === right ? 0 : (left < right ? -1 : 1);
    }
    return `${left}`.localeCompare(`${right}`, undefined, { sensitivity: 'base', numeric: true });
  }

  private getScheduleTimezoneLabel(report: ScheduledReport): string {
    const timezone = this.getDisplayTimezone(report.timezone);
    const referenceValue = report.nextRunAt || new Date().toISOString();
    const referenceDate = new Date(referenceValue);
    if (Number.isNaN(referenceDate.getTime())) {
      return timezone;
    }

    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(referenceDate);
    return parts.find((part) => part.type === 'timeZoneName')?.value || timezone;
  }

  private formatScheduleClockTime(value: string): string {
    const match = `${value || ''}`.trim().match(/^(\d{1,2}):(\d{2})$/);
    if (!match) {
      return `${value || ''}`.trim() || '12:00 AM';
    }

    const hours = Number(match[1]);
    const minutes = match[2];
    if (!Number.isFinite(hours) || hours < 0 || hours > 23) {
      return `${value || ''}`.trim() || '12:00 AM';
    }

    const period = hours >= 12 ? 'PM' : 'AM';
    const normalizedHours = hours % 12 || 12;
    return `${normalizedHours}:${minutes} ${period}`;
  }

  private getDisplayTimezone(timezone?: string | null): string {
    const candidate = `${timezone || ''}`.trim();
    return candidate || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';
  }

  private isSurveyAnswersColumn(header: string): boolean {
    return `${header || ''}`.trim().toLowerCase() === 'answers json';
  }

  private formatSurveyAnswersValue(value: any): string {
    const normalizedValue = this.parseJsonLikeValue(value);
    const formDataAnswers = this.extractFormDataAnswers(normalizedValue);
    if (!formDataAnswers.length) {
      return '';
    }

    return formDataAnswers
      .map((entry: any) => this.formatFormDataAnswerEntry(entry))
      .filter(Boolean)
      .map((entry: string) => this.trimLeadingWhitespaceFromLines(entry))
      .join('\n\n')
      .trim();
  }

  private parseJsonLikeValue(value: any): any {
    if (typeof value !== 'string') {
      return value;
    }

    const trimmed = value.trim();
    if (!trimmed || !['{', '['].includes(trimmed.charAt(0))) {
      return value;
    }

    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  }

  private flattenAnswerLines(value: any, path = ''): string[] {
    if (value === null || value === undefined || value === '') {
      return [];
    }

    if (Array.isArray(value)) {
      if (!value.length) {
        return [];
      }

      if (this.isFormDataAnswerArray(value)) {
        return value
          .map((entry: any) => this.formatFormDataAnswerEntry(entry))
          .filter(Boolean);
      }

      if (value.every((item) => this.isPrimitiveAnswerValue(item))) {
        return path ? [`${path}: ${value.map((item) => this.formatAnswerPrimitive(item)).join(', ')}`] : value.map((item) => this.formatAnswerPrimitive(item));
      }

      return value.reduce((lines: string[], item: any, index: number) => {
        return lines.concat(this.flattenAnswerLines(item, path ? `${path} ${index + 1}` : `${index + 1}`));
      }, []);
    }

    if (typeof value === 'object') {
      return Object.entries(value).reduce((lines: string[], entry: [string, any]) => {
        const [key, nestedValue] = entry;
        const nextPath = path ? `${path} > ${this.humanizeAnswerKey(key)}` : this.humanizeAnswerKey(key);
        return lines.concat(this.flattenAnswerLines(nestedValue, nextPath));
      }, []);
    }

    return path ? [`${path}: ${this.formatAnswerPrimitive(value)}`] : [`${this.formatAnswerPrimitive(value)}`];
  }

  private isPrimitiveAnswerValue(value: any): boolean {
    return value === null || value === undefined || ['string', 'number', 'boolean'].includes(typeof value);
  }

  private isFormDataAnswerArray(value: any[]): boolean {
    return value.every((item) => item && typeof item === 'object' && ('title' in item || 'value' in item));
  }

  private extractFormDataAnswers(value: any): any[] {
    if (Array.isArray(value) && this.isFormDataAnswerArray(value)) {
      return value;
    }

    if (value && typeof value === 'object' && Array.isArray(value.formData) && this.isFormDataAnswerArray(value.formData)) {
      return value.formData;
    }

    return [];
  }

  private formatFormDataAnswerEntry(entry: any): string {
    const title = `${entry?.title || entry?.id || 'Answer'}`.trim();
    const rawValue = this.parseJsonLikeValue(entry?.value);
    const valueText = this.formatFormDataAnswerValue(rawValue);
    return title ? `${title}: ${valueText}` : valueText;
  }

  private trimLeadingWhitespaceFromLines(value: string): string {
    return `${value || ''}`
      .split('\n')
      .map((line) => line.trimStart())
      .join('\n')
      .trim();
  }

  private formatFormDataAnswerValue(value: any): string {
    if (Array.isArray(value)) {
      return value.map((item) => this.formatFormDataAnswerValue(item)).filter(Boolean).join(', ');
    }
    if (value && typeof value === 'object') {
      const nestedLines = this.flattenAnswerLines(value);
      return nestedLines.length ? nestedLines.join(', ') : JSON.stringify(value);
    }
    return this.formatAnswerPrimitive(value);
  }

  private formatAnswerPrimitive(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return `${value}`;
  }

  private humanizeAnswerKey(value: string): string {
    return `${value || ''}`
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private getPreviewSortValue(header: string, value: any): string | number {
    if (value === null || value === undefined) {
      return '';
    }

    const formattedValue = this.getPreviewCellText(header, value).trim();
    const numericValue = Number(formattedValue);
    if (formattedValue !== '' && !Number.isNaN(numericValue) && /^-?\d+(\.\d+)?$/.test(formattedValue)) {
      return numericValue;
    }

    const dateValue = Date.parse(formattedValue);
    if (!Number.isNaN(dateValue) && /\d{4}-\d{2}-\d{2}|[A-Z][a-z]{2}/.test(formattedValue)) {
      return dateValue;
    }

    return formattedValue.toLowerCase();
  }

  private getPreviewTotalHeaders(preview: ScheduledReportPreview | null, target: PreviewSortTarget): string[] {
    const totalColumns = this.getConfiguredReportTotalColumns(target);
    if (!preview || !totalColumns.length) {
      return [];
    }

    return this.getColumnPreviewHeaders(preview).filter((header) => {
      const previewColumn = (preview.columns || []).find((column, index) => (column.header || column.field || `Column ${index + 1}`) === header);
      return totalColumns.some((column) => {
        const configuredHeader = `${column.header || ''}`.trim();
        const configuredField = `${column.field || ''}`.trim();
        return (configuredHeader && configuredHeader === header)
          || (configuredField && previewColumn?.field === configuredField);
      });
    });
  }

  private getConfiguredReportTotalColumns(target: PreviewSortTarget): Array<{ field?: string; header?: string }> {
    if (target === 'detail' && this.detailReport) {
      if (`${this.detailReport.reportType}` === 'template') {
        return ((this.detailReport.templateArgs?.columnOverrides || []) as ScheduledReportTemplateColumnOverride[])
          .filter((column) => column.reportTotal === true);
      }

      const aggregation = this.detailReport.definitionJson?.aggregation;
      if (aggregation?.reportTotal === true) {
        return [{ header: this.getAggregationCountHeader(aggregation) }];
      }

      return (this.detailReport.definitionJson?.columns || []).filter((column) => column.reportTotal === true);
    }

    if (this.isTemplateReport) {
      return this.templateColumnsArray.controls
        .filter((control) => control.get('reportTotal')?.value === true)
        .map((control) => ({
          field: `${control.get('field')?.value || ''}`,
          header: `${control.get('header')?.value || ''}`,
        }));
    }

    if (this.isAggregatedCustomReport) {
      return this.aggregationGroup.get('reportTotal')?.value === true
        ? [{ header: this.getAggregationCountHeader() }]
        : [];
    }

    return this.columnsArray.controls
      .filter((control) => control.get('reportTotal')?.value === true)
      .map((control) => ({
        field: `${control.get('field')?.value || ''}`,
        header: `${control.get('header')?.value || ''}`,
      }));
  }

  private getPreviewTotalLabelHeader(preview: ScheduledReportPreview | null, target: PreviewSortTarget): string {
    const headers = this.getColumnPreviewHeaders(preview);
    const totalHeaders = this.getPreviewTotalHeaders(preview, target);
    if (!headers.length || !totalHeaders.length) {
      return '';
    }

    const firstTotalIndex = headers.findIndex((header) => totalHeaders.includes(header));
    if (firstTotalIndex > 0) {
      return headers[firstTotalIndex - 1];
    }

    return headers[0];
  }

  private getPreviewTotalValue(preview: ScheduledReportPreview | null, header: string): number {
    const backendTotal = this.getBackendPreviewTotalValue(preview, header);
    if (backendTotal !== null) {
      return backendTotal;
    }

    return (preview?.sampleRows || []).reduce((sum, row) => {
      const value = this.getPreviewCellValue(preview, row, header);
      const numericValue = this.parseReportTotalValue(value);
      return numericValue === null ? sum : sum + numericValue;
    }, 0);
  }

  private getBackendPreviewTotalValue(preview: ScheduledReportPreview | null, header: string): number | null {
    if (!preview) {
      return null;
    }

    const column = (preview.columns || []).find((item, index) => (item.header || item.field || `Column ${index + 1}`) === header);
    const possibleKeys = [header, column?.field || ''].filter(Boolean);
    const containers = [preview.totalRow, preview.totals].filter((item): item is Record<string, any> => !!item);
    for (const container of containers) {
      for (const key of possibleKeys) {
        const value = this.parseReportTotalValue(container[key]);
        if (value !== null) {
          return value;
        }
      }
    }

    return null;
  }

  private hasBackendPreviewTotals(preview: ScheduledReportPreview | null): boolean {
    return !!(preview?.totalRow || preview?.totals);
  }

  private parseReportTotalValue(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    const normalized = `${value}`.trim().replace(/[$,%\s,]/g, '');
    if (!normalized) {
      return null;
    }

    const parentheticalMatch = normalized.match(/^\(([-+]?\d+(\.\d+)?)\)$/);
    const numericValue = Number(parentheticalMatch ? `-${parentheticalMatch[1]}` : normalized);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private roundReportTotal(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  private filterItems<T>(items: T[], search: string, getSearchValues: (item: T) => Array<string | null | undefined>): T[] {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) {
      return items;
    }

    return items.filter((item) => getSearchValues(item).some((value) => `${value || ''}`.toLowerCase().includes(normalizedSearch)));
  }

  private presentError(error: any, fallbackMessage: string): void {
    const message = this.getErrorMessage(error, fallbackMessage);
    this.snackBar.open(message, 'Dismiss', { duration: 5000 });
  }

  private getErrorMessage(error: any, fallbackMessage: string): string {
    return this.extractErrorMessage(error) || this.extractErrorMessage(fallbackMessage) || 'Something went wrong.';
  }

  private extractErrorMessage(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return `${value}`;
    }

    if (Array.isArray(value)) {
      return value
        .map((item) => this.extractErrorMessage(item))
        .filter((message) => !!message)
        .join('\n');
    }

    if (typeof value === 'object') {
      for (const key of ['error', 'message', 'detail', 'details', 'title']) {
        const message = this.extractErrorMessage(value[key]);
        if (message) {
          return message;
        }
      }
    }

    return '';
  }
}
